import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';
import SectionBanner from '../components/SectionBanner';
import {
  getSnapshots,
  getSnapshot,
  getBars,
  getNews,
  getOptionContracts,
  getMostActives,
  searchAssets,
  periodToTimeframe,
  placeStockOrder,
  placeOptionOrder,
  getAccount,
  getPositions,
  getOrders,
  getAlpacaAccountId,
  calculateAlpacaCommission,
  comisionOpciones,
  OPCIONES_FEE_POR_EJERCICIO,
  ALPACA_MIN_TRADE_USD,
} from '../lib/alpaca';
import InvestorOnboarding, { PORTFOLIOS } from '../components/InvestorOnboarding';
import PortfolioMark, { portfolioAccent } from '../components/PortfolioMark';
import RoboAdvisor from '../components/RoboAdvisor';
import NewsFeed from '../components/NewsFeed';
import StockAnalysis from '../components/StockAnalysis';
import PortfolioAnalysis from '../components/PortfolioAnalysis';
import PortfolioLiveWidget from '../components/PortfolioLiveWidget';
import StockComparator from '../components/StockComparator';
import PilotoAutomatico from '../components/PilotoAutomatico';
import OraculoChat from '../components/OraculoChat';
import HeroAudioPlayer from '../components/HeroAudioPlayer';
import TickerPopover from '../components/TickerPopover';
import { getPortfolioAudioUrl } from '../lib/oraculoAudio';
import { getPortafolio, getDemoTier, placeStockOrderBackend, ensureAlpacaAccount, getAvailableSymbols, getConditionalOrders, cancelConditionalOrder, listRecurringPlans, pauseRecurringPlan, resumeRecurringPlan, cancelRecurringPlan, getTrm, getFondosProgresion } from '../lib/cerebro';
import { getPosicionInfo, getTickerInfo } from '../lib/tickerInfo';
import { trackStockView, getViewedCount } from '../components/Achievements';
import { listGoals, fmtCop, portfolioLabel } from '../lib/goals';
import oraculoBrujula from '../assets/images/oraculo-brujula.png';

const POPULAR_SYMBOLS = [
  'AAPL', 'MSFT', 'NVDA', 'TSLA', 'AMZN', 'GOOGL', 'META', 'NFLX',
  'PLTR', 'HIMS', 'DUOL', 'AMD', 'COIN', 'SNOW', 'UBER', 'SQ',
  'SHOP', 'CRM', 'PYPL', 'DIS', 'BA', 'JPM', 'V', 'MA',
];
const ETF_SYMBOLS = [
  'VOO', 'QQQ', 'VTI', 'SPY', 'IWM', 'DIA',
  'GLD', 'SLV', 'TLT', 'VWO', 'EEM', 'ARKK',
  'XLF', 'XLE', 'XLK', 'SOXX', 'VNQ', 'BND',
];

const STOCK_NAMES = {
  // Acciones publicadas por el Cerebro (nombres del banco de analisis).
  SPCX: 'SpaceX', MSTR: 'Strategy', CAI: 'Caris Life Sciences', RXRX: 'Recursion Pharmaceuticals',
  TEM: 'Tempus AI', NAUT: 'Nautilus Biotechnology', IBRX: 'ImmunityBio', INKT: 'MiNK Therapeutics',
  NGEN: 'NervGen Pharma', PBLS: 'Parabilis Medicines',
  SCHD: 'Schwab US Dividend Equity ETF',
  AAPL: 'Apple Inc.', MSFT: 'Microsoft Corporation', NVDA: 'NVIDIA Corporation',
  TSLA: 'Tesla Inc.', AMZN: 'Amazon.com Inc.', GOOGL: 'Alphabet Inc.', META: 'Meta Platforms Inc.',
  NFLX: 'Netflix Inc.', PLTR: 'Palantir Technologies', HIMS: 'Hims & Hers Health',
  DUOL: 'Duolingo Inc.', AMD: 'Advanced Micro Devices', COIN: 'Coinbase Global',
  SNOW: 'Snowflake Inc.', UBER: 'Uber Technologies', SQ: 'Block Inc. (Square)',
  SHOP: 'Shopify Inc.', CRM: 'Salesforce Inc.', PYPL: 'PayPal Holdings',
  DIS: 'Walt Disney Company', BA: 'Boeing Company', F: 'Ford Motor Company', JPM: 'JPMorgan Chase',
  V: 'Visa Inc.', MA: 'Mastercard Inc.', WMT: 'Walmart Inc.',
  KO: 'Coca-Cola Company', PEP: 'PepsiCo Inc.', NKE: 'Nike Inc.',
  INTC: 'Intel Corporation', ORCL: 'Oracle Corporation', ADBE: 'Adobe Inc.',
  CSCO: 'Cisco Systems', QCOM: 'Qualcomm Inc.', TXN: 'Texas Instruments',
  AVGO: 'Broadcom Inc.', COST: 'Costco Wholesale', HD: 'Home Depot',
  MCD: 'McDonald\'s Corp.', SBUX: 'Starbucks Corp.', PG: 'Procter & Gamble',
  JNJ: 'Johnson & Johnson', UNH: 'UnitedHealth Group', LLY: 'Eli Lilly',
  PFE: 'Pfizer Inc.', ABBV: 'AbbVie Inc.', MRK: 'Merck & Co.',
  XOM: 'Exxon Mobil', CVX: 'Chevron Corporation', GS: 'Goldman Sachs',
  MS: 'Morgan Stanley', BAC: 'Bank of America', WFC: 'Wells Fargo',
  C: 'Citigroup Inc.', 'BRK.B': 'Berkshire Hathaway', T: 'AT&T Inc.',
  VZ: 'Verizon Communications', TMUS: 'T-Mobile US', ABNB: 'Airbnb Inc.',
  SNAP: 'Snap Inc.', PINS: 'Pinterest Inc.', SPOT: 'Spotify Technology',
  ZM: 'Zoom Video', ROKU: 'Roku Inc.', RBLX: 'Roblox Corp.',
  U: 'Unity Software', DKNG: 'DraftKings Inc.', RIVN: 'Rivian Automotive',
  LCID: 'Lucid Group', NIO: 'NIO Inc.', LI: 'Li Auto Inc.',
  SOFI: 'SoFi Technologies', HOOD: 'Robinhood Markets', MELI: 'MercadoLibre',
  NU: 'Nu Holdings', SE: 'Sea Limited', GRAB: 'Grab Holdings', GLOB: 'Globant',
  BABA: 'Alibaba Group', JD: 'JD.com Inc.', PDD: 'PDD Holdings (Temu)',
  TSM: 'Taiwan Semiconductor', ASML: 'ASML Holding', ARM: 'Arm Holdings',
  SMCI: 'Super Micro Computer', DELL: 'Dell Technologies', PANW: 'Palo Alto Networks',
  CRWD: 'CrowdStrike Holdings', NET: 'Cloudflare Inc.', DDOG: 'Datadog Inc.',
  MDB: 'MongoDB Inc.', TEAM: 'Atlassian Corp.', NOW: 'ServiceNow Inc.',
  WDAY: 'Workday Inc.', ZS: 'Zscaler Inc.', OKTA: 'Okta Inc.',
  TTD: 'The Trade Desk', CPNG: 'Coupang Inc.', AFRM: 'Affirm Holdings',
  PATH: 'UiPath Inc.', AI: 'C3.ai Inc.', IONQ: 'IonQ Inc.',
  RGTI: 'Rigetti Computing', LUNR: 'Intuitive Machines', RKLB: 'Rocket Lab',
  VOO: 'Vanguard S&P 500 ETF', QQQ: 'Invesco QQQ Trust', VTI: 'Vanguard Total Stock',
  SPY: 'SPDR S&P 500 ETF', IWM: 'iShares Russell 2000', DIA: 'SPDR Dow Jones ETF',
  GLD: 'SPDR Gold Shares', SLV: 'iShares Silver Trust', TLT: 'iShares 20+ Year Treasury',
  VWO: 'Vanguard FTSE Emerging', EEM: 'iShares MSCI Emerging', ARKK: 'ARK Innovation ETF',
  XLF: 'Financial Select SPDR', XLE: 'Energy Select SPDR', XLK: 'Technology Select SPDR',
  SOXX: 'iShares Semiconductor', VNQ: 'Vanguard Real Estate', BND: 'Vanguard Total Bond',
};

const formatPrice = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatPct = (n) => `${n >= 0 ? '+' : ''}${Number(n).toFixed(2)}%`;


/** Rank ticker/name matches (espejo del scorer de investments-api/alpaca). */
function escapeRegExpQuery(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function scoreAssetMatch(query, item) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return -1;
  const symbol = String(item?.symbol || '');
  const name = String(item?.name || '');
  const s = symbol.toLowerCase();
  const n = name.toLowerCase();
  const firstWord = (n.split(/[\s.,&/-]+/).filter(Boolean)[0] || '');
  let score = -1;
  if (s === q || firstWord === q) score = 1000;
  else if (s.startsWith(q)) score = 900 - Math.min(symbol.length, 80);
  else if (n.startsWith(q)) score = 800;
  else if (new RegExp('(^|[\\s.,&/-])' + escapeRegExpQuery(q)).test(n)) score = 700;
  else if (n.includes(q) || s.includes(q)) score = 400;
  return score;
}
function rankAssetMatches(query, items, limit = 12) {
  const q = String(query || '').trim();
  if (!q) return [];
  const scored = [];
  const seen = new Set();
  for (const it of items || []) {
    if (!it?.symbol || seen.has(it.symbol)) continue;
    const score = scoreAssetMatch(q, it);
    if (score < 0) continue;
    seen.add(it.symbol);
    scored.push({
      symbol: it.symbol,
      name: it.name || it.symbol,
      _score: score,
      _symLen: String(it.symbol).length,
      _name: String(it.name || it.symbol).toLowerCase(),
    });
  }
  scored.sort((a, b) => {
    if (b._score !== a._score) return b._score - a._score;
    if (a._symLen !== b._symLen) return a._symLen - b._symLen;
    return a._name.localeCompare(b._name);
  });
  return scored.slice(0, limit).map(({ symbol, name }) => ({ symbol, name }));
}


/** Mini sparkline from live dailyBar OHLC (real market data, not inventado). */
function SparkFromSnap({ snap, isPos }) {
  const b = snap?.dailyBar;
  const vals = b ? [b.o, b.h, b.l, b.c].map(Number).filter((n) => Number.isFinite(n) && n > 0) : [];
  if (vals.length < 2) {
    return <svg className={`mercado-ola__spark ${isPos ? '' : 'down'}`} viewBox="0 0 64 24" aria-hidden="true"><path d="M2 12 H62" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.35"/></svg>;
  }
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * 62 + 1;
    const y = 22 - ((v - min) / range) * 18;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg className={`mercado-ola__spark ${isPos ? '' : 'down'}`} viewBox="0 0 64 24" aria-hidden="true">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StockRow({ symbol, snap, onClick, active }) {
  const price = snap?.latestTrade?.p || snap?.dailyBar?.c || 0;
  const prevClose = snap?.prevDailyBar?.c || 0;
  const change = price - prevClose;
  const pct = prevClose ? (change / prevClose) * 100 : 0;
  const isPos = pct >= 0;
  const hasPrice = Boolean(snap) && price > 0;
  return (
    <button
      type="button"
      onClick={() => onClick(symbol)}
      className={`mercado-ola__row ${active ? 'on' : ''}`}
    >
      <div className="min-w-0">
        <div className="mercado-ola__sym">{symbol}</div>
        <div className="mercado-ola__nm">{STOCK_NAMES[symbol] || ''}</div>
      </div>
      <SparkFromSnap snap={snap} isPos={isPos} />
      <div>
        <div className="mercado-ola__px">{hasPrice ? formatPrice(price) : '—'}</div>
        <div className={`mercado-ola__chg ${hasPrice ? (isPos ? 'up' : 'down') : ''}`}>{hasPrice ? formatPct(pct) : '—'}</div>
      </div>
    </button>
  );
}

function StockList({ symbols, snapshots, onSelect, activeSymbol }) {
  return (
    <div className="mercado-ola__panel">
      <div className="mercado-ola__list-note">Precios en vivo · mercado</div>
      {symbols.map((s) => (
        <StockRow key={s} symbol={s} snap={snapshots[s]} onClick={onSelect} active={activeSymbol === s} />
      ))}
    </div>
  );
}

function MarketDetailPanel({ symbol, snap, onBuy, onSell, period, setPeriod, bars }) {
  if (!symbol || !snap) {
    return (
      <div className="mercado-ola__panel">
        <p className="mercado-ola__hint" style={{ marginTop: 0 }}>Selecciona una acción o ETF de la lista para ver detalle y operar.</p>
      </div>
    );
  }
  const price = snap.latestTrade?.p || snap.dailyBar?.c || 0;
  const prevClose = snap.prevDailyBar?.c || 0;
  const change = price - prevClose;
  const pct = prevClose ? (change / prevClose) * 100 : 0;
  const isPos = pct >= 0;
  const isEtf = ETF_SYMBOLS.includes(symbol) || String(STOCK_NAMES[symbol] || '').toUpperCase().includes('ETF');
  return (
    <div className="mercado-ola__panel mercado-ola__detail">
      <div className="mercado-ola__detail-top">
        <div>
          <div className="mercado-ola__detail-sym">{symbol}</div>
          <div className="mercado-ola__detail-nm">{STOCK_NAMES[symbol] || ''}</div>
        </div>
        <span className="mercado-ola__kind">{isEtf ? 'ETF' : 'Acción'}</span>
      </div>
      <div className="mercado-ola__detail-price">
        <span>{formatPrice(price)}</span>
        <span className={`mercado-ola__chg ${isPos ? 'up' : 'down'}`}>{formatPct(pct)} hoy</span>
      </div>
      <div className="mercado-ola__ranges">
        {['1D', '1S', '1M', '3M', '1A', 'Max'].map((p) => (
          <button key={p} type="button" className={period === p ? 'on' : ''} onClick={() => setPeriod(p)}>{p}</button>
        ))}
      </div>
      <div style={{ marginBottom: 8 }}>
        <MiniChart bars={bars} isPos={isPos} />
      </div>
      <div className="mercado-ola__trade">
        <button type="button" className="buy" onClick={() => onBuy(symbol)}>Comprar</button>
        <button type="button" className="sell" onClick={() => onSell(symbol)}>Vender</button>
      </div>
      <div className="mercado-ola__fraction">Desde $50.000 COP · fracciones</div>
      <p className="mercado-ola__hint">El Oráculo informa sobre tu plata; no aconseja comprar ni vender.</p>
    </div>
  );
}

function MiniChart({ bars, isPos }) {
  if (!bars || bars.length === 0) {
    return <div className="h-64 flex items-center justify-center text-[#9CA3AF] text-xs">Sin datos</div>;
  }
  const prices = bars.map((b) => b.c);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const pad = range * 0.08;
  const yMin = min - pad;
  const yMax = max + pad;
  const yRange = yMax - yMin;
  const w = 700, h = 240, ml = 65, mr = 15, mt = 10, mb = 25;
  const cw = w - ml - mr, ch = h - mt - mb;
  const color = isPos ? '#16a34a' : '#dc2626';
  const bgColor = isPos ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)';

  // Y-axis ticks (5 levels)
  const yTicks = [];
  for (let i = 0; i <= 4; i++) {
    const val = yMin + (yRange * i) / 4;
    const y = mt + ch - (ch * i) / 4;
    yTicks.push({ val, y });
  }

  // X-axis labels (5 dates)
  const xLabels = [];
  const step = Math.max(1, Math.floor(bars.length / 4));
  for (let i = 0; i < bars.length; i += step) {
    const x = ml + (i / (prices.length - 1)) * cw;
    const d = new Date(bars[i].t);
    const label = d.toLocaleDateString('es', { day: '2-digit', month: 'short' });
    xLabels.push({ x, label });
  }

  const points = prices.map((p, i) => {
    const x = ml + (i / (prices.length - 1)) * cw;
    const y = mt + ch - ((p - yMin) / yRange) * ch;
    return `${x},${y}`;
  });
  const line = points.join(' ');
  const firstX = ml;
  const lastX = ml + cw;
  const fillPoints = `${firstX},${mt + ch} ${line} ${lastX},${mt + ch}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-64">
      {/* Grid lines */}
      {yTicks.map((t, i) => (
        <line key={i} x1={ml} y1={t.y} x2={w - mr} y2={t.y} stroke="#F0F0F0" strokeWidth="1" />
      ))}
      {/* Y-axis labels */}
      {yTicks.map((t, i) => (
        <text key={i} x={ml - 8} y={t.y + 4} textAnchor="end" fontSize="11" fill="#9CA3AF" fontFamily="system-ui">
          ${t.val.toFixed(2)}
        </text>
      ))}
      {/* X-axis labels */}
      {xLabels.map((t, i) => (
        <text key={i} x={t.x} y={h - 5} textAnchor="middle" fontSize="10" fill="#9CA3AF" fontFamily="system-ui">
          {t.label}
        </text>
      ))}
      {/* Fill area */}
      <polygon points={fillPoints} fill={bgColor} />
      {/* Line */}
      <polyline points={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
      {/* Start and end dots */}
      {points.length > 0 && (
        <>
          <circle cx={points[0].split(',')[0]} cy={points[0].split(',')[1]} r="3" fill={color} />
          <circle cx={points[points.length-1].split(',')[0]} cy={points[points.length-1].split(',')[1]} r="4" fill={color} stroke="white" strokeWidth="2" />
        </>
      )}
    </svg>
  );
}

function StockDetail({ symbol, onBack, accountId, tabInicial }) {
  const [snap, setSnap] = useState(null);
  const [bars, setBars] = useState([]);
  const [news, setNews] = useState([]);
  const [period, setPeriod] = useState('1M');
  // El Oraculo puede mandar directo a Opciones: "analiza las opciones de PLTR"
  // debe ABRIR la pestaña, no describirla.
  const [tab, setTab] = useState(tabInicial || 'accion');
  const [optType, setOptType] = useState('call');
  const [contracts, setContracts] = useState([]);
  const [allContracts, setAllContracts] = useState([]);
  const [expDates, setExpDates] = useState([]);
  const [selectedExp, setSelectedExp] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [optMode, setOptMode] = useState('buy');
  const [optQty, setOptQty] = useState('1');
  const [buyAmt, setBuyAmt] = useState('');
  const [buyMode, setBuyMode] = useState('buy');
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [orderError, setOrderError] = useState(null);
  const [optOrderLoading, setOptOrderLoading] = useState(false);
  const [optOrderResult, setOptOrderResult] = useState(null);
  const [optOrderError, setOptOrderError] = useState(null);
  const [newsError, setNewsError] = useState(false);

  useEffect(() => {
    trackStockView(symbol);
    let cancelled = false;
    const fetchSnapshot = async () => {
      try {
        const snaps = await getSnapshots([symbol]);
        if (!cancelled) setSnap(snaps[symbol]);
      } catch (e) { console.error('snapshot:', e); }
    };
    fetchSnapshot();
    const interval = setInterval(fetchSnapshot, 30000);
    (async () => {
      try {
        const n = await getNews([symbol], 8);
        if (!cancelled) setNews(n);
      } catch (e) { console.error('news:', e); if (!cancelled) setNewsError(true); }
    })();
    return () => { cancelled = true; clearInterval(interval); };
  }, [symbol]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { timeframe, days } = periodToTimeframe(period);
        const start = new Date(Date.now() - days * 86400000).toISOString();
        const b = await getBars(symbol, timeframe, start);
        if (!cancelled) setBars(b);
      } catch (e) { console.error('bars:', e); }
    })();
    return () => { cancelled = true; };
  }, [symbol, period]);

  useEffect(() => {
    if (tab !== 'opciones') return;
    let cancelled = false;
    (async () => {
      try {
        const c = await getOptionContracts(symbol, optType);
        if (!cancelled) {
          setAllContracts(c);
          // Extract unique expiration dates
          const dates = [...new Set(c.map((x) => x.expiration_date))].sort();
          setExpDates(dates);
          if (dates.length > 0 && !dates.includes(selectedExp)) {
            setSelectedExp(dates[0]);
          }
        }
      } catch (e) { console.error('options:', e); }
    })();
    return () => { cancelled = true; };
  }, [symbol, optType, tab]);

  useEffect(() => {
    if (!selectedExp || allContracts.length === 0) return;
    const filtered = allContracts
      .filter((c) => c.expiration_date === selectedExp)
      .sort((a, b) => parseFloat(a.strike_price) - parseFloat(b.strike_price));
    setContracts(filtered);
  }, [selectedExp, allContracts]);

  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => { if (!snap) setLoadError(true); }, 8000);
    return () => clearTimeout(timer);
  }, [snap]);

  if (!snap && loadError) return (
    <div className="p-4 sm:p-8 text-center">
      <div className="text-lg font-bold text-[#181D27] mb-2">No se encontraron datos para {symbol}</div>
      <div className="text-sm text-[#9CA3AF] mb-4">Es posible que el símbolo no esté disponible en el mercado o que el mercado esté cerrado.</div>
      <button onClick={onBack} className="px-6 py-2.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold">← Volver</button>
    </div>
  );

  if (!snap) return <div className="p-4 sm:p-8 text-center text-[#9CA3AF]">Cargando {symbol}...</div>;

  const price = snap.latestTrade?.p || 0;
  const prevClose = snap.prevDailyBar?.c || 0;
  const change = price - prevClose;
  const pct = prevClose ? (change / prevClose) * 100 : 0;
  const isPos = pct >= 0;
  const dayBar = snap.dailyBar || {};
  const quote = snap.latestQuote || {};

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-20 sm:pb-6">
      <button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold text-[#2A59C6] hover:underline mb-4 sm:mb-5">
        ← Volver a Inversiones
      </button>

      <div className="flex items-center gap-3 mb-3 sm:mb-4">
        <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl bg-[#F5F5F5] flex items-center justify-center text-base sm:text-lg font-bold text-[#2A59C6]">
          {symbol.slice(0, 2)}
        </div>
        <div>
          <div className="text-xl sm:text-2xl font-extrabold">{symbol}</div>
          <div className="text-xs sm:text-sm text-[#6B7280]">{STOCK_NAMES[symbol] || ''}</div>
        </div>
      </div>

      <div className="mb-4 sm:mb-6">
        <div className="text-3xl sm:text-4xl font-extrabold">{formatPrice(price)}</div>
        <div className={`text-sm sm:text-base font-semibold ${isPos ? 'text-green-600' : 'text-red-600'}`}>
          {isPos ? '↑' : '↓'} {change.toFixed(2)} ({formatPct(pct)}) Hoy
        </div>
      </div>

      <div className="flex gap-0 border-b-2 border-[#E5E7EB] mb-5">
        <button onClick={() => setTab('accion')} className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-0.5 ${tab === 'accion' ? 'text-[#2A59C6] border-[#2A59C6]' : 'text-[#6B7280] border-transparent'}`}>
          Acción
        </button>
        <button onClick={() => setTab('opciones')} className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-0.5 ${tab === 'opciones' ? 'text-[#2A59C6] border-[#2A59C6]' : 'text-[#6B7280] border-transparent'}`}>
          Opciones
        </button>
        <button onClick={() => setTab('news')} className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-0.5 ${tab === 'news' ? 'text-[#2A59C6] border-[#2A59C6]' : 'text-[#6B7280] border-transparent'}`}>
          Noticias
        </button>
      </div>

      {tab === 'accion' && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          <div>
            <div className="flex gap-1 mb-3">
              {['1D', '1S', '1M', '3M', '1A', 'Max'].map((p) => (
                <button key={p} onClick={() => setPeriod(p)} className={`px-3.5 py-1.5 rounded-full text-xs font-semibold ${period === p ? 'bg-[#2A59C6] text-white' : 'text-[#6B7280] hover:bg-[#F5F5F5]'}`}>
                  {p}
                </button>
              ))}
            </div>
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 mb-5">
              <MiniChart bars={bars} isPos={isPos} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3.5">
                <div className="text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider">Cierre anterior</div>
                <div className="text-base font-bold">{formatPrice(prevClose)}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3.5">
                <div className="text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider">Apertura</div>
                <div className="text-base font-bold">{formatPrice(dayBar.o || 0)}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3.5">
                <div className="text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider">Volumen</div>
                <div className="text-base font-bold">{(dayBar.v || 0).toLocaleString()}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3.5">
                <div className="text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider">Máximo hoy</div>
                <div className="text-base font-bold text-green-600">{formatPrice(dayBar.h || 0)}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3.5">
                <div className="text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider">Mínimo hoy</div>
                <div className="text-base font-bold text-red-600">{formatPrice(dayBar.l || 0)}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3.5">
                <div className="text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider">VWAP</div>
                <div className="text-base font-bold">{dayBar.vw ? formatPrice(dayBar.vw) : '-'}</div>
              </div>
            </div>
            {/* Cerebro Analysis */}
            <StockAnalysis symbol={symbol} />
          </div>

          <div>
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sticky top-5">
              <div className="flex border border-[#E5E7EB] rounded-lg overflow-hidden mb-4">
                <button onClick={() => setBuyMode('buy')} className={`flex-1 py-2.5 text-sm font-semibold ${buyMode === 'buy' ? 'bg-[#2A59C6] text-white' : 'text-[#6B7280]'}`}>Comprar</button>
                <button onClick={() => setBuyMode('sell')} className={`flex-1 py-2.5 text-sm font-semibold ${buyMode === 'sell' ? 'bg-[#2A59C6] text-white' : 'text-[#6B7280]'}`}>Vender</button>
              </div>
              <label className="block text-xs font-semibold text-[#6B7280] mb-1.5">Monto en USD</label>
              <div className="flex items-center border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 mb-4">
                <span className="text-lg font-bold text-[#9CA3AF] mr-1">$</span>
                <input type="number" value={buyAmt} onChange={(e) => setBuyAmt(e.target.value)} placeholder="0.00" className="flex-1 outline-none text-xl font-bold" />
              </div>
              <div className="flex gap-1.5 mb-4">
                {[12, 25, 50, 100, 500].map((a) => (
                  <button key={a} onClick={() => setBuyAmt(String(a))} className="flex-1 py-2 border border-[#E5E7EB] rounded-md text-xs font-semibold text-[#2A59C6] hover:bg-[#E6EBF7]">${a}</button>
                ))}
              </div>
              {buyAmt && parseFloat(buyAmt) > 0 && parseFloat(buyAmt) < ALPACA_MIN_TRADE_USD && (
                <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg p-3 mb-3 text-xs text-[#991B1B]">
                  Inversión mínima: <strong>${ALPACA_MIN_TRADE_USD} USD</strong>. Aumenta el monto para continuar.
                </div>
              )}
              {buyAmt && parseFloat(buyAmt) >= ALPACA_MIN_TRADE_USD && (() => {
                const amt = parseFloat(buyAmt);
                const c = calculateAlpacaCommission(amt);
                const total = amt + c.feeTotal;
                const tierLabel = c.tier === 'tier1' ? 'Plana $12-$50' : c.tier === 'tier2' ? 'Plana $50-$500' : c.tier === 'cap' ? 'Tope maximo' : '0,3% sobre el monto';
                return (
                  <div className="bg-[#FAFAFA] rounded-lg p-3.5 mb-3 text-xs">
                    <div className="flex justify-between py-0.5 text-[#6B7280]"><span>Monto invertido</span><span>{formatPrice(amt)}</span></div>
                    <div className="flex justify-between py-0.5 text-[#6B7280]"><span>Acciones estimadas</span><span>{(amt / price).toFixed(6)}</span></div>
                    <div className="flex justify-between py-0.5 text-[#6B7280]"><span>Comision base <span className="text-[10px] text-[#9CA3AF]">({tierLabel})</span></span><span>${c.feeBase.toFixed(2)}</span></div>
                    <div className="flex justify-between py-0.5 text-[#6B7280]"><span>IVA 19%</span><span>${c.iva.toFixed(2)}</span></div>
                    <div className="flex justify-between pt-2.5 mt-1.5 border-t border-[#E5E7EB] font-bold"><span>Total a debitar</span><span>{formatPrice(total)}</span></div>
                    <div className="mt-2 text-[10px] text-[#9CA3AF] leading-snug">Recibiras factura electronica al correo. Comision efectiva: {c.effectivePct.toFixed(2)}%.</div>
                  </div>
                );
              })()}
              <div className="bg-[#FEF3C7] rounded-lg px-3.5 py-2.5 text-xs text-center mb-3 text-[#92400E]">
                Invierte desde $50,000 COP — compra fraccionada
              </div>
              <button
                disabled={!buyAmt || parseFloat(buyAmt) <= 0 || orderLoading}
                onClick={async () => {
                  setOrderLoading(true);
                  setOrderResult(null);
                  setOrderError(null);
                  try {
                    // Va al BACKEND (/api/orders/stock): calcula y cobra la comision
                    // escalonada + IVA via Fireblocks, registra el Trade y factura Siigo.
                    const result = await placeStockOrderBackend({
                      symbol,
                      notional: parseFloat(buyAmt),
                      side: buyMode,
                    });
                    setOrderResult(result);
                  } catch (e) {
                    setOrderError(e.response?.data?.message || e.message || 'Error al procesar la orden');
                  }
                  setOrderLoading(false);
                }}
                className={`w-full py-3.5 rounded-xl text-sm font-bold text-white ${buyMode === 'buy' ? 'bg-[#2A59C6]' : 'bg-red-500'} disabled:opacity-35 disabled:cursor-not-allowed`}
              >
                {orderLoading ? 'Procesando...' : `${buyMode === 'buy' ? 'Comprar' : 'Vender'} ${symbol}`}
              </button>
              {orderResult && (
                <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs">
                  <div className="font-bold text-green-700 mb-1">Orden enviada</div>
                  <div className="text-green-600">ID: {orderResult.id?.slice(0, 8)}...</div>
                  <div className="text-green-600">Estado: {orderResult.status}</div>
                  <div className="text-green-600">{orderResult.side?.toUpperCase()} {orderResult.symbol} · ${orderResult.notional || orderResult.qty}</div>
                </div>
              )}
              {orderError && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs">
                  <div className="font-bold text-red-700 mb-1">Error</div>
                  <div className="text-red-600">{orderError}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'opciones' && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="flex gap-2">
                <button onClick={() => { setOptType('call'); setSelectedOption(null); }} className={`px-5 py-2 rounded-full text-xs font-semibold border ${optType === 'call' ? 'bg-green-600 text-white border-green-600' : 'border-[#E5E7EB] text-[#6B7280]'}`}>Calls</button>
                <button onClick={() => { setOptType('put'); setSelectedOption(null); }} className={`px-5 py-2 rounded-full text-xs font-semibold border ${optType === 'put' ? 'bg-red-600 text-white border-red-600' : 'border-[#E5E7EB] text-[#6B7280]'}`}>Puts</button>
              </div>
              {expDates.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-[#6B7280]">Vencimiento:</span>
                  <select
                    value={selectedExp}
                    onChange={(e) => { setSelectedExp(e.target.value); setSelectedOption(null); }}
                    className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-sm font-semibold text-[#2A59C6] outline-none focus:border-[#2A59C6] cursor-pointer"
                  >
                    {expDates.map((d) => (
                      <option key={d} value={d}>
                        {new Date(d + 'T12:00:00').toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-[#9CA3AF]">({expDates.length} fechas)</span>
                </div>
              )}
            </div>

            <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden max-h-[500px] overflow-y-auto">
              <div className="grid grid-cols-[1fr_1.2fr_1fr_0.8fr] px-4 py-2.5 bg-[#FAFAFA] text-[11px] font-bold text-[#6B7280] uppercase tracking-wider sticky top-0 z-10">
                <span>Strike Price</span><span>Prima (Último)</span><span>Open Interest</span><span>Tipo</span>
              </div>
              {contracts.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#9CA3AF]">Cargando contratos...</div>
              ) : (
                contracts.map((c) => {
                  const strikeNum = parseFloat(c.strike_price);
                  const isITM = optType === 'call' ? strikeNum < price : strikeNum > price;
                  const isSelected = selectedOption?.symbol === c.symbol;
                  return (
                    <div
                      key={c.symbol}
                      onClick={() => { setSelectedOption(c); setOptQty('1'); }}
                      className={`grid grid-cols-[1fr_1.2fr_1fr_0.8fr] px-4 py-3 border-t border-[#F5F5F5] text-sm cursor-pointer transition ${isSelected ? 'bg-[#E6EBF7] border-l-4 border-l-[#2A59C6]' : isITM ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-[#FAFAFA]'}`}
                    >
                      <div>
                        <span className="font-bold text-[#2A59C6]">${c.strike_price}</span>
                        {isITM && <span className="ml-2 text-[10px] font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded">ITM</span>}
                      </div>
                      <div>
                        <span className="font-semibold">{c.close_price ? `$${Number(c.close_price).toFixed(2)}` : '-'}</span>
                        {c.close_price && <span className="text-xs text-[#9CA3AF] ml-1">x100 = ${(Number(c.close_price) * 100).toFixed(0)}</span>}
                      </div>
                      <span className="text-[#6B7280]">{c.open_interest || '-'}</span>
                      <span className={`text-xs font-semibold ${optType === 'call' ? 'text-green-600' : 'text-red-600'}`}>{optType === 'call' ? 'Call' : 'Put'}</span>
                    </div>
                  );
                })
              )}
            </div>
            <div className="mt-3 text-xs text-[#9CA3AF]">
              ITM = In The Money (precio actual: {formatPrice(price)}) · Selecciona un contrato para operar
            </div>
          </div>

          {/* Options order panel */}
          <div>
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sticky top-5">
              {!selectedOption ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">👆</div>
                  <div className="text-sm font-semibold text-[#6B7280]">Selecciona un contrato</div>
                  <div className="text-xs text-[#9CA3AF] mt-1">Haz click en una opción para operar</div>
                </div>
              ) : (
                <>
                  <div className="flex border border-[#E5E7EB] rounded-lg overflow-hidden mb-4">
                    <button onClick={() => setOptMode('buy')} className={`flex-1 py-2.5 text-sm font-semibold ${optMode === 'buy' ? 'bg-[#2A59C6] text-white' : 'text-[#6B7280]'}`}>Comprar</button>
                    <button onClick={() => setOptMode('sell')} className={`flex-1 py-2.5 text-sm font-semibold ${optMode === 'sell' ? 'bg-orange-500 text-white' : 'text-[#6B7280]'}`}>Emitir</button>
                  </div>

                  <div className="bg-[#FAFAFA] rounded-lg p-3.5 mb-4 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-[#6B7280]">Contrato</span>
                      <span className="font-bold text-[#2A59C6]">{symbol} {optType === 'call' ? 'Call' : 'Put'} ${selectedOption.strike_price}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#6B7280]">Vencimiento</span>
                      <span className="font-semibold">{new Date(selectedOption.expiration_date + 'T12:00:00').toLocaleDateString('es', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#6B7280]">Prima por acción</span>
                      <span className="font-semibold">{selectedOption.close_price ? `$${Number(selectedOption.close_price).toFixed(2)}` : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#6B7280]">Multiplicador</span>
                      <span className="font-semibold">x100 acciones</span>
                    </div>
                  </div>

                  <label className="block text-xs font-semibold text-[#6B7280] mb-1.5">Contratos</label>
                  <div className="flex items-center border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 mb-3">
                    <button onClick={() => setOptQty(String(Math.max(1, parseInt(optQty || '1') - 1)))} className="w-8 h-8 rounded-lg bg-[#F5F5F5] flex items-center justify-center text-lg font-bold text-[#6B7280] hover:bg-[#E5E7EB]">-</button>
                    <input type="number" value={optQty} onChange={(e) => setOptQty(e.target.value)} min="1" className="flex-1 outline-none text-xl font-bold text-center" />
                    <button onClick={() => setOptQty(String(parseInt(optQty || '1') + 1))} className="w-8 h-8 rounded-lg bg-[#F5F5F5] flex items-center justify-center text-lg font-bold text-[#6B7280] hover:bg-[#E5E7EB]">+</button>
                  </div>

                  {selectedOption.close_price && parseInt(optQty) > 0 && (
                    <div className="bg-[#FAFAFA] rounded-lg p-3.5 mb-3 text-xs space-y-1">
                      <div className="flex justify-between text-[#6B7280]">
                        <span>Prima por contrato</span>
                        <span>${(Number(selectedOption.close_price) * 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-[#6B7280]">
                        <span>Contratos</span>
                        <span>x{parseInt(optQty) || 1}</span>
                      </div>
                      {/* Decia "$0 GRATIS", que era falso: nadie habia definido la
                          comision de opciones y quedo ese texto de relleno. Se
                          aplica la MISMA escalera que las acciones
                          (utils/pricing.js, IVA incluido) sobre el valor de la
                          operacion. Prometerle "gratis" a un cliente y despues
                          cobrarle es lo peor que podriamos hacer. */}
                      {/* La tarifa de opciones NO es la escalera de acciones: es
                          por CONTRATO, y estaba definida en el modelo financiero
                          (Supuestos, seccion 5) — solo que no estaba en el
                          codigo, por eso la pantalla llego a decir "GRATIS". */}
                      {(() => {
                        const n = parseInt(optQty) || 1;
                        const bruto = Number(selectedOption.close_price) * 100 * n;
                        const { feeTotal, porContrato } = comisionOpciones(n);
                        const total = optMode === 'buy' ? bruto + feeTotal : bruto - feeTotal;
                        return (
                          <>
                            <div className="flex justify-between text-[#6B7280]">
                              <span>Comisión ({n} × ${porContrato.toFixed(2)}, IVA incl.)</span>
                              <span className="font-semibold text-[#181D27]">${feeTotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between pt-2 mt-1 border-t border-[#E5E7EB] font-bold text-sm">
                              <span>{optMode === 'buy' ? 'Costo total' : 'Prima neta que recibes'}</span>
                              <span>{formatPrice(total)}</span>
                            </div>
                            {/* Se dice ANTES de comprar: si la opcion termina
                                ejerciendose, hay otro cobro. Enterarse despues
                                es la peor forma de enterarse. */}
                            <div className="text-[10px] text-[#9CA3AF] pt-1 leading-snug">
                              Si la ejerces o te la asignan, se cobran ${OPCIONES_FEE_POR_EJERCICIO.toFixed(2)} más por posición.
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {optMode === 'buy' ? (
                    <div className="bg-[#EFF6FF] rounded-lg px-3.5 py-2.5 text-xs text-center mb-3 text-[#1E40AF]">
                      Derecho a {optType === 'call' ? 'comprar' : 'vender'} {(parseInt(optQty) || 1) * 100} acciones de {symbol} a ${selectedOption.strike_price}
                    </div>
                  ) : (
                    <div className="bg-[#FFF7ED] rounded-lg px-3.5 py-2.5 text-xs text-center mb-3 text-[#9A3412]">
                      Obligación de {optType === 'call' ? 'vender' : 'comprar'} {(parseInt(optQty) || 1) * 100} acciones de {symbol} a ${selectedOption.strike_price}
                    </div>
                  )}

                  <button
                    disabled={optOrderLoading}
                    onClick={async () => {
                      setOptOrderLoading(true);
                      setOptOrderResult(null);
                      setOptOrderError(null);
                      try {
                        const result = await placeOptionOrder({
                          symbol: selectedOption.symbol,
                          qty: parseInt(optQty) || 1,
                          side: optMode === 'buy' ? 'buy' : 'sell',
                          accountId,
                          primaUsd: Number(selectedOption.close_price) || undefined,
                        });
                        setOptOrderResult(result);
                      } catch (e) {
                        const code = e.response?.data?.code;
                        const msg = e.response?.data?.message || e.message || 'Error al procesar la orden';
                        setOptOrderError(code === 40310000 ? 'Opciones no habilitadas en sandbox. Contactar soporte Alpaca.' : msg);
                      }
                      setOptOrderLoading(false);
                    }}
                    className={`w-full py-3.5 rounded-xl text-sm font-bold text-white ${optMode === 'buy' ? 'bg-[#2A59C6]' : 'bg-orange-500'} disabled:opacity-35`}
                  >
                    {optOrderLoading ? 'Procesando...' : optMode === 'buy' ? `Comprar ${parseInt(optQty) || 1} contrato${(parseInt(optQty) || 1) > 1 ? 's' : ''}` : `Emitir ${parseInt(optQty) || 1} contrato${(parseInt(optQty) || 1) > 1 ? 's' : ''}`}
                  </button>
                  {optOrderResult && (
                    <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs">
                      <div className="font-bold text-green-700 mb-1">Orden enviada</div>
                      <div className="text-green-600">ID: {optOrderResult.id?.slice(0, 8)}...</div>
                      <div className="text-green-600">Estado: {optOrderResult.status}</div>
                    </div>
                  )}
                  {optOrderError && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs">
                      <div className="font-bold text-red-700 mb-1">Error</div>
                      <div className="text-red-600">{optOrderError}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'news' && (
        <div className="space-y-3">
          {news.length === 0 && !newsError && <div className="text-sm text-[#9CA3AF]">Cargando noticias...</div>}
          {news.length === 0 && newsError && (
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center">
              <div className="text-lg font-bold text-[#181D27] mb-2">Noticias no disponibles</div>
              <div className="text-sm text-[#6B7280] mb-4">El plan actual de datos no incluye noticias del mercado.</div>
              <a href={`https://finance.yahoo.com/quote/${symbol}/news/`} target="_blank" rel="noopener noreferrer" className="inline-block px-5 py-2.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8] transition">
                Ver noticias en Yahoo Finance
              </a>
            </div>
          )}
          {news.map((n) => (
            <a key={n.id} href={n.url} target="_blank" rel="noopener noreferrer" className="block bg-white border border-[#E5E7EB] rounded-xl p-5 hover:border-[#2A59C6] transition">
              <div className="flex items-start gap-4">
                {n.images?.[0]?.url && (
                  <img src={n.images[0].url} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#6B7280] mb-1">{n.source} · {new Date(n.created_at).toLocaleDateString('es')}</div>
                  <div className="text-sm font-semibold text-[#181D27] mb-1.5">{n.headline}</div>
                  <div className="text-xs text-[#6B7280] line-clamp-2">{n.summary}</div>
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// "Tu dinero por meta" — reparte el dinero invertido entre las metas del cliente
// y muestra con que portafolio trabaja cada una. Autonomo: se nutre solo de las
// metas (mismo valor que ve en "Mis Metas"), no toca el flujo del portafolio.
const PORTFOLIO_COLOR = {
  Guardian: '#2A9D6B', Explorer: '#3d8bfd', Visionary: '#E85D4C', 'Income Builder': '#C9A961',
};
function GoalAllocation() {
  const [goals, setGoals] = useState(null);
  const [trm, setTrm] = useState(0); // 1 USD en COP — para el total dual (SPEC multimoneda)
  useEffect(() => {
    let alive = true;
    listGoals().then((g) => alive && setGoals(g || [])).catch(() => alive && setGoals([]));
    getTrm().then((r) => alive && r > 0 && setTrm(r)).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!goals) return null;

  const rows = goals
    .filter((g) => (g.status ? g.status !== 'archived' : true))
    .map((g) => {
      const p = g.projection || {};
      return {
        id: g._id || g.id || g.name,
        name: g.name,
        valueCop: Math.round(Number(p.currentCop || 0)),
        portfolio: portfolioLabel(p.portfolioType),
        portfolioKey: p.portfolioType,
      };
    })
    .filter((r) => r.valueCop > 0)
    .sort((a, b) => b.valueCop - a.valueCop);

  if (rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + r.valueCop, 0);

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 mb-6 sm:mb-8">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-lg font-bold text-[#181D27]">Tu dinero por meta</h2>
        <span className="text-sm font-semibold text-[#6B7280]">
          {fmtCop(total)} COP
          {trm > 0 && <span className="text-[#9CA3AF] font-normal"> · ~${(total / trm).toLocaleString('en-US', { maximumFractionDigits: 0 })} USD</span>}
        </span>
      </div>
      <p className="text-xs text-[#6B7280] mb-4">Así está repartido tu dinero entre tus metas, y con qué portafolio trabaja cada una.</p>
      <div className="space-y-3">
        {rows.map((r) => {
          const pct = total > 0 ? (r.valueCop / total) * 100 : 0;
          const color = PORTFOLIO_COLOR[r.portfolioKey] || '#2A59C6';
          return (
            <div key={r.id}>
              <div className="flex items-center justify-between mb-1 gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-semibold text-[#181D27] truncate">{r.name}</span>
                  {r.portfolio && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: color + '1A', color }}>{r.portfolio}</span>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <span className="text-sm font-bold text-[#181D27]">{fmtCop(r.valueCop)}</span>
                  <span className="text-xs text-[#6B7280] ml-2">{Math.round(pct)}%</span>
                </div>
              </div>
              <div className="h-2 rounded-full bg-[#F1F1F1] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.max(pct, 3)}%`, backgroundColor: color }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// F1.3 — estado claro cuando la cuenta de inversion aun no esta lista, en vez de
// mostrar un portafolio vacio/roto. Mensaje + accion segun el caso.
function PortfolioProvisionState({ state }) {
  const cfg = {
    no_profile: {
      title: 'Arma tu portafolio primero',
      msg: 'Responde unas preguntas rapidas y te asignamos tu portafolio ideal. Luego activamos tu cuenta de inversion automaticamente.',
      cta: 'Empezar',
    },
    provisioning: {
      title: 'Estamos activando tu cuenta',
      msg: 'Tu cuenta de inversion se esta creando. Esto toma unos segundos.',
      cta: 'Actualizar',
    },
    failed: {
      title: 'No pudimos activar tu cuenta',
      msg: 'Hubo un problema activando tu cuenta de inversion. Intenta de nuevo en un momento.',
      cta: 'Reintentar',
    },
  }[state] || {};
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center max-w-lg mx-auto my-8">
      <div className="text-base font-bold text-[#181D27] mb-2">{cfg.title}</div>
      <div className="text-sm text-[#6B7280] mb-5">{cfg.msg}</div>
      <button
        onClick={() => window.location.reload()}
        className="px-5 py-2.5 rounded-lg bg-[#2A59C6] text-white text-sm font-semibold hover:bg-[#1f47a3] transition"
      >
        {cfg.cta}
      </button>
    </div>
  );
}

function Portfolio({ onSelectStock, accountId }) {
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  // Ordenes condicionales VIGILANDO (las crea el Oraculo; viven en Mongo, no en el broker)
  const [conditionals, setConditionals] = useState([]);
  const [cancelingCond, setCancelingCond] = useState(null);
  // Planes del Piloto Automatico (los crea el Oraculo; el cron los ejecuta solo)
  const [pilots, setPilots] = useState([]);
  const [busyPilot, setBusyPilot] = useState(null);
  const [loadingPortfolio, setLoadingPortfolio] = useState(true);
  const [provisionState, setProvisionState] = useState(null); // null | 'no_profile' | 'provisioning' | 'failed'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Asegura (idempotente) que el usuario del beta tenga su cuenta Alpaca + saldo
        // de practica. Barato si ya existe. Cubre a quien entro sin cuenta antes.
        const ensured = await ensureAlpacaAccount();
        if (cancelled) return;
        if (ensured?.provisioning) { setProvisionState('provisioning'); setLoadingPortfolio(false); return; }
        if (ensured && !ensured.ok) {
          // Solo bloqueamos con CTA si el backend dice claramente que no hay cuenta.
          // BACKEND_DOWN u otros: seguimos e intentamos cargar (puede ya tener cuenta).
          if (ensured.code === 'NO_PROFILE') { setProvisionState('no_profile'); setLoadingPortfolio(false); return; }
          if (ensured.code === 'PROVISION_FAILED') { setProvisionState('failed'); setLoadingPortfolio(false); return; }
        }

        const [acc, pos, ord] = await Promise.all([
          getAccount(accountId),
          getPositions(accountId),
          getOrders('all', 20, accountId),
        ]);
        if (!cancelled) {
          setAccount(acc);
          setPositions(pos);
          setOrders(ord);
        }

        // Condicionales vigilando — carga aparte y tolerante a fallos:
        // si el endpoint no responde, el portafolio sigue completo.
        const conds = await getConditionalOrders();
        if (!cancelled) setConditionals(conds);

        // Planes del Piloto Automatico — misma tolerancia a fallos.
        const rp = await listRecurringPlans();
        const plans = (rp?.data || []).filter((p) => p.status === 'active' || p.status === 'paused');
        if (!cancelled) setPilots(plans);
      } catch (e) {
        // Error transitorio cargando el portafolio: NO bloqueamos con CTA (el usuario
        // ya tiene cuenta si llego aqui); se muestra el estado vacio normal.
        console.error('portfolio:', e);
      } finally {
        if (!cancelled) setLoadingPortfolio(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loadingPortfolio) return <div className="text-center py-10 text-[#9CA3AF]">Cargando portafolio...</div>;
  if (provisionState) return <PortfolioProvisionState state={provisionState} />;

  const equity = parseFloat(account?.equity || 0);
  const cash = parseFloat(account?.cash || 0);
  const buyingPower = parseFloat(account?.buying_power || 0);
  const portfolioValue = parseFloat(account?.portfolio_value || 0);
  const totalPL = positions.reduce((sum, p) => sum + parseFloat(p.unrealized_pl || 0), 0);
  const totalPLPct = equity > 0 ? (totalPL / (equity - totalPL)) * 100 : 0;

  return (
    <div>
      {/* Account summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 sm:p-5">
          <div className="text-[10px] sm:text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider mb-1">Valor del portafolio · USD</div>
          <div className="text-lg sm:text-2xl font-extrabold text-[#181D27]">{formatPrice(portfolioValue)}</div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 sm:p-5">
          <div className="text-[10px] sm:text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider mb-1">Efectivo · USD</div>
          <div className="text-lg sm:text-2xl font-extrabold text-[#181D27]">{formatPrice(cash)}</div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 sm:p-5">
          <div className="text-[10px] sm:text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider mb-1">Poder de compra · USD</div>
          <div className="text-lg sm:text-2xl font-extrabold text-[#2A59C6]">{formatPrice(buyingPower)}</div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 sm:p-5">
          <div className="text-[10px] sm:text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider mb-1">Ganancia / Pérdida · USD</div>
          <div className={`text-lg sm:text-2xl font-extrabold ${totalPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {totalPL >= 0 ? '+' : ''}{formatPrice(totalPL)}
          </div>
          <div className={`text-xs font-semibold ${totalPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatPct(totalPLPct)}</div>
        </div>
      </div>

      {/* Tu dinero por meta */}
      <GoalAllocation />

      {/* Positions */}
      <h2 className="text-lg font-bold mb-4">Mis Posiciones</h2>
      {positions.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center mb-8">
          <div className="text-sm text-[#9CA3AF]">No tienes posiciones abiertas. Compra una acción para empezar.</div>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden mb-8 overflow-x-auto">
          <div className="min-w-[700px]">
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] px-5 py-3 bg-[#FAFAFA] text-[11px] font-bold text-[#6B7280] uppercase tracking-wider">
            <span>Activo</span><span className="text-right">Cantidad</span><span className="text-right">Precio actual</span><span className="text-right">Costo promedio</span><span className="text-right">Valor de mercado</span><span className="text-right">Ganancia</span>
          </div>
          {positions.map((p) => {
            const pl = parseFloat(p.unrealized_pl || 0);
            const plPct = parseFloat(p.unrealized_plpc || 0) * 100;
            const qty = parseFloat(p.qty || p.qty_available || 0);
            const currentPrice = parseFloat(p.current_price || 0);
            const avgEntry = parseFloat(p.avg_entry_price || 0);
            const marketValue = parseFloat(p.market_value || 0);
            return (
              <div
                key={p.asset_id}
                onClick={() => onSelectStock(p.symbol)}
                className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] px-5 py-4 border-t border-[#F5F5F5] cursor-pointer hover:bg-[#FAFAFA] transition items-center"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center text-xs font-bold text-[#2A59C6]">{p.symbol.slice(0, 2)}</div>
                  <div>
                    <div className="text-sm font-bold text-[#2A59C6]">{p.symbol}</div>
                    <div className="text-xs text-[#6B7280] truncate">{STOCK_NAMES[p.symbol] || ''}</div>
                  </div>
                </div>
                <div className="text-right text-sm font-semibold">{qty < 1 ? qty.toFixed(6) : qty.toFixed(2)}</div>
                <div className="text-right text-sm font-semibold">{formatPrice(currentPrice)}</div>
                <div className="text-right text-sm text-[#6B7280]">{formatPrice(avgEntry)}</div>
                <div className="text-right text-sm font-semibold">{formatPrice(marketValue)}</div>
                <div className="text-right">
                  <div className={`text-sm font-bold ${pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {pl >= 0 ? '+' : ''}{formatPrice(pl)}
                  </div>
                  <div className={`text-xs font-semibold ${pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatPct(plPct)}</div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}

      {/* Ordenes condicionales VIGILANDO (sinteticas, creadas con el Oraculo).
          Antes solo se veian preguntandole al chat; ahora viven en pantalla. */}
      {conditionals.length > 0 && (
        <>
          <h2 className="text-lg font-bold mb-1">Órdenes inteligentes</h2>
          <p className="text-xs text-[#6B7280] mb-4">El Oráculo vigila el precio minuto a minuto y ejecuta por ti cuando se cumpla la condición.</p>
          <div className="bg-white border border-[#C9A961] rounded-xl overflow-hidden mb-8">
            {conditionals.map((c, i) => {
              const desc = c.side === 'buy'
                ? (c.condition === 'below' ? `Compra $${Number(c.notional).toLocaleString('en-US')} si baja a $${c.triggerPrice}` : `Compra $${Number(c.notional).toLocaleString('en-US')} si rompe $${c.triggerPrice}`)
                : (c.condition === 'above' ? `Vende si sube a $${c.triggerPrice}` : `Stop loss: vende si baja a $${c.triggerPrice}`);
              return (
                <div key={c.id} className={`flex items-center justify-between px-5 py-4 ${i > 0 ? 'border-t border-[#F5F5F5]' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#FFFBF0] border border-[#C9A961] flex items-center justify-center text-xs font-bold text-[#8B6F3D]">{c.symbol.slice(0, 2)}</div>
                    <div>
                      <div className="text-sm font-bold text-[#0A2540]">{c.symbol}</div>
                      <div className="text-xs text-[#6B7280]">{desc}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-[#FFFBF0] text-[#8B6F3D] border border-[#E8D9B5]">Vigilando</span>
                    <button
                      type="button"
                      disabled={cancelingCond === c.id}
                      onClick={async () => {
                        if (!window.confirm(`¿Cancelar la orden condicional de ${c.symbol}? (${desc})`)) return;
                        setCancelingCond(c.id);
                        const r = await cancelConditionalOrder(c.id);
                        setCancelingCond(null);
                        if (r.success) setConditionals((prev) => prev.filter((x) => x.id !== c.id));
                        else alert(r.error || 'No se pudo cancelar. Intenta de nuevo.');
                      }}
                      className="text-xs font-semibold text-[#B91C1C] hover:underline disabled:opacity-40"
                    >
                      {cancelingCond === c.id ? 'Cancelando…' : 'Cancelar'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Piloto Automatico: planes recurrentes creados con el Oraculo.
          El cliente ve QUE compra, CUANTO, CADA CUANTO y HASTA CUANDO. */}
      {pilots.length > 0 && (
        <>
          <h2 className="text-lg font-bold mb-1">Piloto Automático</h2>
          <p className="text-xs text-[#6B7280] mb-4">Tus inversiones recurrentes: el Oráculo compra por ti en la fecha programada, sin que hagas nada.</p>
          <div className="bg-white border border-[#2A59C6] rounded-xl overflow-hidden mb-8">
            {pilots.map((p, i) => {
              const nombre = p.nickname || p.symbol;
              const monto = p.planType === 'goal' && p.amountCop
                ? `$${Number(p.amountCop).toLocaleString('es-CO')} COP`
                : `$${Number(p.notional).toFixed(0)} USD`;
              const fBogota = (d) => new Date(d).toLocaleDateString('es-CO', { timeZone: 'America/Bogota', day: 'numeric', month: 'short', year: 'numeric' });
              const proxima = p.nextExecutionDate ? fBogota(p.nextExecutionDate) : '—';
              const hasta = p.endDate ? fBogota(p.endDate) : null;
              const activo = p.status === 'active';
              return (
                <div key={p.id} className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 ${i > 0 ? 'border-t border-[#F5F5F5]' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#EFF4FF] border border-[#2A59C6] flex items-center justify-center text-xs font-bold text-[#2A59C6]">{String(p.symbol || '').slice(0, 2)}</div>
                    <div>
                      <div className="text-sm font-bold text-[#0A2540]">{nombre}</div>
                      <div className="text-xs text-[#6B7280]">
                        {monto} {p.frequencyLabel || ''} · próxima: {proxima}{hasta ? ` · hasta: ${hasta}` : ''}
                      </div>
                      <div className="text-[11px] text-[#9CA3AF]">
                        {p.totalExecutions || 0} ejecucion{(p.totalExecutions || 0) === 1 ? '' : 'es'} · ${Number(p.totalInvestedUsd || 0).toFixed(2)} USD invertidos
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[11px] font-bold px-2 py-1 rounded-full border ${activo ? 'bg-[#ECFDF3] text-[#12B76A] border-[#A6F4C5]' : 'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]'}`}>
                      {activo ? 'Activo' : 'Pausado'}
                    </span>
                    <button
                      type="button"
                      disabled={busyPilot === p.id}
                      onClick={async () => {
                        setBusyPilot(p.id);
                        const r = activo ? await pauseRecurringPlan(p.id) : await resumeRecurringPlan(p.id);
                        setBusyPilot(null);
                        if (r.success) setPilots((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: activo ? 'paused' : 'active' } : x)));
                        else alert(r.message || 'No se pudo. Intenta de nuevo.');
                      }}
                      className="text-xs font-semibold text-[#2A59C6] hover:underline disabled:opacity-40"
                    >
                      {busyPilot === p.id ? '…' : activo ? 'Pausar' : 'Reanudar'}
                    </button>
                    <button
                      type="button"
                      disabled={busyPilot === p.id}
                      onClick={async () => {
                        if (!window.confirm(`¿Cancelar el Piloto de ${nombre}? (${monto} ${p.frequencyLabel || ''})`)) return;
                        setBusyPilot(p.id);
                        const r = await cancelRecurringPlan(p.id);
                        setBusyPilot(null);
                        if (r.success) setPilots((prev) => prev.filter((x) => x.id !== p.id));
                        else alert(r.message || 'No se pudo cancelar. Intenta de nuevo.');
                      }}
                      className="text-xs font-semibold text-[#B91C1C] hover:underline disabled:opacity-40"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Recent orders */}
      <h2 className="text-lg font-bold mb-4">Órdenes Recientes</h2>
      {orders.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center">
          <div className="text-sm text-[#9CA3AF]">No hay órdenes recientes.</div>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden overflow-x-auto">
          <div className="min-w-[650px]">
          <div className="grid grid-cols-[1.5fr_0.8fr_1fr_1fr_1fr_1fr] px-5 py-3 bg-[#FAFAFA] text-[11px] font-bold text-[#6B7280] uppercase tracking-wider">
            <span>Activo</span><span>Tipo</span><span className="text-right">Cantidad</span><span className="text-right">Precio</span><span className="text-right">Total</span><span className="text-right">Estado</span>
          </div>
          {orders.slice(0, 15).map((o) => {
            const filledQty = parseFloat(o.filled_qty || 0);
            const filledAvg = parseFloat(o.filled_avg_price || 0);
            const notional = o.notional ? parseFloat(o.notional) : filledQty * filledAvg;
            const statusMap = {
              new: 'Nuevo', accepted: 'Aceptada', filled: 'Ejecutada',
              partially_filled: 'Parcial', canceled: 'Cancelada',
              expired: 'Expirada', rejected: 'Rechazada', pending_new: 'Pendiente',
            };
            const statusColor = {
              filled: 'text-green-600 bg-green-50', accepted: 'text-blue-600 bg-blue-50',
              new: 'text-blue-600 bg-blue-50', canceled: 'text-[#9CA3AF] bg-[#F5F5F5]',
              rejected: 'text-red-600 bg-red-50', expired: 'text-[#9CA3AF] bg-[#F5F5F5]',
              partially_filled: 'text-orange-600 bg-orange-50', pending_new: 'text-blue-600 bg-blue-50',
            };
            return (
              <div key={o.id} className="grid grid-cols-[1.5fr_0.8fr_1fr_1fr_1fr_1fr] px-5 py-3.5 border-t border-[#F5F5F5] items-center text-sm">
                <div>
                  <span className="font-bold text-[#2A59C6]">{o.symbol}</span>
                  <span className="text-xs text-[#9CA3AF] ml-2">{new Date(o.created_at).toLocaleDateString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div>
                  <span className={`text-xs font-bold px-2 py-1 rounded ${o.side === 'buy' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
                    {o.side === 'buy' ? 'Compra' : 'Venta'}
                  </span>
                </div>
                <div className="text-right font-semibold">
                  {filledQty > 0 ? (filledQty < 1 ? filledQty.toFixed(6) : filledQty.toFixed(2)) : (o.notional ? `$${parseFloat(o.notional).toFixed(2)}` : o.qty)}
                </div>
                <div className="text-right text-[#6B7280]">{filledAvg > 0 ? formatPrice(filledAvg) : '-'}</div>
                <div className="text-right font-semibold">{notional > 0 ? formatPrice(notional) : '-'}</div>
                <div className="text-right">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusColor[o.status] || 'text-[#6B7280] bg-[#F5F5F5]'}`}>
                    {statusMap[o.status] || o.status}
                  </span>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}

const LEVELS = [
  { name: 'Novato', minXP: 0 },
  { name: 'Inversionista', minXP: 3 },
  { name: 'Trader', minXP: 6 },
  { name: 'Experto', minXP: 9 },
  { name: 'Leyenda', minXP: 11 },
];

function AchievementMini() {
  const streak = (() => { try { return JSON.parse(localStorage.getItem('fp_invest_streak') || '{}').weeks || 0; } catch (e) { return 0; } })();
  const hasProfile = !!localStorage.getItem('fp_investor_profile');
  const roboUsed = !!localStorage.getItem('fp_robo_used');
  const viewedStocks = getViewedCount();
  const referrals = parseInt(localStorage.getItem('fp_referrals') || '0');

  // Count unlocked (simplified — no API calls, just local data)
  let count = 0;
  if (hasProfile) count++;
  if (roboUsed) count++;
  if (viewedStocks >= 10) count++;
  if (referrals >= 1) count++;
  if (referrals >= 5) count++;
  if (referrals >= 10) count++;

  const level = LEVELS.slice().reverse().find(l => count >= l.minXP) || LEVELS[0];
  const icon = level.name === 'Novato' ? '🌱' : level.name === 'Inversionista' ? '📈' : level.name === 'Trader' ? '⚡' : level.name === 'Leyenda' ? '🏆' : '👑';

  if (count === 0 && streak === 0) return null;

  return (
    <div className="mb-3 flex items-center gap-3 text-xs text-[#6B7280]">
      <span className="flex items-center gap-1 bg-[#F0F4FF] text-[#2A59C6] px-2.5 py-1 rounded-full font-semibold">
        {icon} {level.name}
      </span>
      {streak > 0 && (
        <span className="flex items-center gap-1 bg-[#FFF7ED] text-[#EA580C] px-2.5 py-1 rounded-full font-semibold">
          🔥 {streak} {streak === 1 ? 'semana' : 'semanas'}
        </span>
      )}
      <span>{count} logros</span>
    </div>
  );
}

export default function Investments() {
  const snap = useSnapshot(state);
  const userEmail = snap.user?.email || '';
  const accountId = getAlpacaAccountId(userEmail);

  const [snapshots, setSnapshots] = useState({});
  const [movers, setMovers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null); // 'opciones' cuando llega desde el Oraculo

  // El Oraculo pide abrir una accion en una pestaña concreta. Se hace con un
  // evento para no acoplar el modal de voz con esta pantalla.
  useEffect(() => {
    const abrir = (e) => {
      const { symbol, tab } = e.detail || {};
      if (!symbol) return;
      setSelectedTab(tab || null);
      setSelected(String(symbol).toUpperCase());
    };
    window.addEventListener('flowex:abrir-accion', abrir);
    return () => window.removeEventListener('flowex:abrir-accion', abrir);
  }, []);
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('trend');
  const [focusSymbol, setFocusSymbol] = useState(null);
  const [detailPeriod, setDetailPeriod] = useState('1M');
  const [detailBars, setDetailBars] = useState([]);
  const [analyzed, setAnalyzed] = useState([]); // acciones con analisis del Cerebro (lista real del backend)
  const [geoFilter, setGeoFilter] = useState('internacional'); // 'internacional' (EEUU) | 'colombia' (BVC)
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab');
  const [mainTab, setMainTab] = useState(
    ['mercado', 'portafolio', 'plan', 'piloto', 'comparar', 'oraculo'].includes(initialTab) ? initialTab : 'mercado'
  );
  // Ticker popover — null o symbol (string). Cuando hay symbol, renderiza el popup.
  const [tickerPopover, setTickerPopover] = useState(null);
  // El portafolio con sus DOS patas (perfil + moneda). Se pide al catalogo del
  // backend: la pantalla no vuelve a tener su propia copia de composiciones.
  const [portMulti, setPortMulti] = useState(null);


  // Investor profile / onboarding
  const [investorProfile, setInvestorProfile] = useState(() => {
    try {
      const saved = localStorage.getItem('fp_investor_profile');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [showOnboarding, setShowOnboarding] = useState(!investorProfile);

  // Trae el portafolio multimoneda cuando el perfil ya tiene portafolioId.
  // VA AQUI, DESPUES de declarar investorProfile: puesto antes reventaba en
  // runtime (const no existe hasta su linea). Los perfiles viejos, sin ese
  // campo, no piden nada y la pantalla sigue mostrando lo de siempre.
  useEffect(() => {
    const id = investorProfile?.portafolioId;
    if (!id) { setPortMulti(null); return; }
    let vivo = true;
    getPortafolio(id).then((p) => { if (vivo && p?.posiciones) setPortMulti(p); });
    return () => { vivo = false; };
  }, [investorProfile?.portafolioId]);

  const [showRoboAdvisor, setShowRoboAdvisor] = useState(false);

  const handleOnboardingComplete = (profile) => {
    if (profile) {
      setInvestorProfile(profile);
    }
    setShowOnboarding(false);
  };

  useEffect(() => {
    let cancelled = false;
    const fetchPrices = async () => {
      try {
        const av = await getAvailableSymbols();
        if (!cancelled) setAnalyzed(av);
        const m = await getMostActives(20);
        const moverSymbols = (m || []).map((x) => x.symbol).filter(Boolean);
        const all = [...new Set([...POPULAR_SYMBOLS, ...ETF_SYMBOLS, ...av, ...moverSymbols])];
        const snaps = await getSnapshots(all);
        if (!cancelled) {
          setMovers(moverSymbols);
          setSnapshots(snaps);
        }
      } catch (e) {
        console.error('init:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!search || search.length < 1) { setSearchResults([]); return; }

    // Local: rankear STOCK_NAMES (ticker + nombre)
    const localItems = Object.entries(STOCK_NAMES).map(([symbol, name]) => ({ symbol, name }));
    const localRanked = rankAssetMatches(search, localItems, 12);
    setSearchResults(localRanked);

    // Remoto: universo Alpaca completo — preferir orden/scores remotos al fusionar
    if (search.length >= 2) {
      const timer = setTimeout(async () => {
        setSearching(true);
        try {
          const remote = await searchAssets(search); // [{symbol, name}]
          if (remote.length > 0) {
            const bySym = new Map();
            for (const r of remote) bySym.set(r.symbol, r);
            for (const l of localRanked) {
              if (!bySym.has(l.symbol)) bySym.set(l.symbol, l);
            }
            setSearchResults(rankAssetMatches(search, [...bySym.values()], 12));
          }
        } catch { /* keep local results */ }
        setSearching(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [search, movers]);

  // El portafolio que se EJECUTA tiene que ser el mismo que se VE. Mientras
  // esto pasaba el legacy, la pantalla decia "40% en pesos / 60% en dolares" y
  // el Robo Advisor compraba el Explorer viejo, 100% en dolares y con otros
  // pesos. El cliente aprobaba una cosa y se ejecutaba otra.
  const planEjecutable = portMulti
    ? { ...portMulti, name: portMulti.nombre, risk: portMulti.riesgo, icon: portMulti.icono }
    : (investorProfile?.portfolio ? PORTFOLIOS[investorProfile.portfolio] : null);

  // Barras del panel detalle (datos reales via getBars)
  useEffect(() => {
    let alive = true;
    let list;
    switch (cat) {
      case 'trend': {
        if (movers.length >= 8) list = movers;
        else list = [...new Set([...movers, ...POPULAR_SYMBOLS])].slice(0, Math.max(12, movers.length + POPULAR_SYMBOLS.length));
        break;
      }
      case 'etf': list = ETF_SYMBOLS; break;
      case 'analyzed': list = analyzed; break;
      default: list = POPULAR_SYMBOLS; break;
    }
    const sym = (focusSymbol && list.includes(focusSymbol)) ? focusSymbol : (list[0] || null);
    if (!sym || cat === 'lists' || cat === 'col') { setDetailBars([]); return; }
    try {
      const { timeframe, days } = periodToTimeframe(detailPeriod);
      const start = new Date(Date.now() - days * 86400000).toISOString();
      getBars(sym, timeframe, start, undefined, 120)
        .then((bars) => { if (alive) setDetailBars(bars || []); })
        .catch(() => { if (alive) setDetailBars([]); });
    } catch {
      if (alive) setDetailBars([]);
    }
    return () => { alive = false; };
  }, [focusSymbol, detailPeriod, movers, cat, analyzed]);

  if (showRoboAdvisor && planEjecutable) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-20 sm:pb-6">
        <RoboAdvisor
          portfolio={planEjecutable}
          onClose={() => setShowRoboAdvisor(false)}
        />
      </div>
    );
  }

  if (showOnboarding) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-20 sm:pb-6">
        <InvestorOnboarding onComplete={handleOnboardingComplete} />
      </div>
    );
  }

  if (selected) {
    return (
      <StockDetail
        symbol={selected}
        tabInicial={selectedTab}
        onBack={() => { setSelected(null); setSelectedTab(null); }}
        accountId={accountId}
      />
    );
  }

  const cats = [
    { id: 'trend', label: 'En tendencia' },
    { id: 'etf', label: 'ETFs' },
    { id: 'lists', label: 'Tus listas' },
    { id: 'col', label: 'Acciones COL' },
    ...(analyzed.length ? [{ id: 'analyzed', label: 'Analizadas' }] : []),
  ];

  const getList = () => {
    switch (cat) {
      case 'trend': {
        if (movers.length >= 8) return movers;
        const pad = [...new Set([...movers, ...POPULAR_SYMBOLS])];
        return pad.slice(0, Math.max(12, pad.length));
      }
      case 'etf': return ETF_SYMBOLS;
      case 'analyzed': return analyzed;
      case 'popular': return POPULAR_SYMBOLS;
      case 'movers': return movers;
      default: return POPULAR_SYMBOLS;
    }
  };

  // Prefill focus with first symbol that has a snapshot
  const listSymbols = getList();
  const effectiveFocus = focusSymbol && listSymbols.includes(focusSymbol)
    ? focusSymbol
    : (listSymbols.find((s) => snapshots[s]) || listSymbols[0] || null);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-20 sm:pb-6">
      <div className="mb-4 sm:mb-6">
        <h1
          className="text-xl sm:text-2xl font-bold mb-1 select-none"
          onDoubleClick={() => { localStorage.removeItem('fp_investor_profile'); setInvestorProfile(null); setShowOnboarding(true); }}
        >Inversiones</h1>
        <p className="text-xs sm:text-sm" style={{ color: 'var(--fx-muted)' }}>Acciones, ETFs y opciones — desde $50,000 COP, compra fraccionada</p>
      </div>

      {/* Mini achievement banner */}
      <AchievementMini />

      {/* Portfolio recommendation banner */}
      {investorProfile && investorProfile.portfolio && PORTFOLIOS[investorProfile.portfolio] && (
        <div
          className="mb-4 sm:mb-6 p-4 rounded-xl cursor-pointer hover:shadow-sm transition"
          style={{ background: 'var(--fx-card)', border: '1px solid var(--fx-border)' }}
          onClick={() => setMainTab('plan')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PortfolioMark portfolio={investorProfile.portfolio} size={40} />
              <div>
                <div className="text-sm font-bold" style={{ color: 'var(--fx-text)' }}>Portafolio {portMulti?.nombre || PORTFOLIOS[investorProfile.portfolio].name}</div>
                <div className="text-xs" style={{ color: 'var(--fx-muted)' }}>{PORTFOLIOS[investorProfile.portfolio].risk} · {PORTFOLIOS[investorProfile.portfolio].expectedReturn}</div>
              </div>
            </div>
            <div className="text-xs font-semibold" style={{ color: 'var(--fx-accent)' }}>Ver plan ›</div>
          </div>
        </div>
      )}

      <SectionBanner
        id="investments"
        title="Invierte en empresas de EE. UU."
        text="Compra fracciones de Apple, Nvidia o fondos desde $50.000. El Oráculo analiza cada una contigo antes de decidir."
      />

      {/* Main tabs: Mercado / Portafolio / Mi Plan */}
      <div className="flex gap-0 border-b-2 mb-4 sm:mb-6 overflow-x-auto" style={{ borderColor: 'var(--fx-border)' }}>
        <button onClick={() => setMainTab('mercado')} className={`px-3 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold border-b-2 -mb-0.5 whitespace-nowrap ${mainTab === 'mercado' ? 'text-[var(--fx-accent)] border-[var(--fx-accent)]' : 'border-transparent hover:text-[var(--fx-accent)]'}`} style={mainTab === 'mercado' ? undefined : { color: 'var(--fx-muted)' }}>
          Mercado
        </button>
        <button onClick={() => setMainTab('portafolio')} className={`px-3 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold border-b-2 -mb-0.5 whitespace-nowrap ${mainTab === 'portafolio' ? 'text-[var(--fx-accent)] border-[var(--fx-accent)]' : 'border-transparent hover:text-[var(--fx-accent)]'}`} style={mainTab === 'portafolio' ? undefined : { color: 'var(--fx-muted)' }}>
          Mi Portafolio
        </button>
        <button onClick={() => setMainTab('plan')} className={`px-3 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold border-b-2 -mb-0.5 whitespace-nowrap ${mainTab === 'plan' ? 'text-[var(--fx-accent)] border-[var(--fx-accent)]' : 'border-transparent hover:text-[var(--fx-accent)]'}`} style={mainTab === 'plan' ? undefined : { color: 'var(--fx-muted)' }}>
          Mi Plan
        </button>
        <button onClick={() => setMainTab('piloto')} className={`px-3 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold border-b-2 -mb-0.5 whitespace-nowrap ${mainTab === 'piloto' ? 'text-[var(--fx-accent)] border-[var(--fx-accent)]' : 'border-transparent hover:text-[var(--fx-accent)]'}`} style={mainTab === 'piloto' ? undefined : { color: 'var(--fx-muted)' }}>
          Piloto
        </button>
        <button onClick={() => setMainTab('comparar')} className={`px-3 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold border-b-2 -mb-0.5 whitespace-nowrap ${mainTab === 'comparar' ? 'text-[var(--fx-accent)] border-[var(--fx-accent)]' : 'border-transparent hover:text-[var(--fx-accent)]'}`} style={mainTab === 'comparar' ? undefined : { color: 'var(--fx-muted)' }}>
          Comparador
        </button>
        <button onClick={() => setMainTab('oraculo')} className={`px-3 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold border-b-2 -mb-0.5 whitespace-nowrap flex items-center gap-1.5 ${mainTab === 'oraculo' ? 'text-[var(--fx-accent)] border-[var(--fx-accent)]' : 'border-transparent hover:text-[var(--fx-accent)]'}`} style={mainTab === 'oraculo' ? undefined : { color: 'var(--fx-muted)' }}>
          Oráculo
          <span className="px-1 py-0.5 text-[8px] font-bold rounded uppercase tracking-wide" style={{ backgroundColor: '#3d8bfd', color: '#fff' }}>Nuevo</span>
        </button>
      </div>

      {mainTab === 'piloto' && (
        <PilotoAutomatico />
      )}

      {mainTab === 'comparar' && (
        <div className="max-w-4xl">
          <StockComparator />
        </div>
      )}

      {mainTab === 'oraculo' && (
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-4 px-1">
            <img src={oraculoBrujula} alt="" className="w-10 h-10 rounded-full object-cover" style={{ boxShadow: '0 0 18px rgba(61,139,253,0.45)' }} />
            <div>
              <div className="text-sm font-bold" style={{ color: 'var(--fx-text)' }}>Oráculo</div>
              <div className="text-xs" style={{ color: 'var(--fx-muted)' }}>Informa sobre tu plata · voz e historial intactos</div>
            </div>
          </div>
          <div className="fx-glass border rounded-xl overflow-hidden shadow-sm" style={{ height: '600px', borderColor: 'rgba(61,139,253,0.28)' }}>
            <OraculoChat embedded />
          </div>
        </div>
      )}

      {mainTab === 'plan' && (
        <div className="max-w-lg">
          {/* Hero card promocional del Oraculo */}
          <button
            onClick={() => setMainTab('oraculo')}
            className="w-full text-left rounded-2xl p-4 mb-4 border transition-all hover:shadow-lg"
            style={{ background: 'linear-gradient(135deg, #0A2540 0%, #132f4c 100%)', borderColor: '#C9A961' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1a3a5c 50%, #C9A961 100%)' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93z" fill="white" opacity="0.9"/></svg>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-white">Habla con el Oraculo</span>
                  <span className="px-1.5 py-0.5 text-[8px] font-bold rounded uppercase" style={{ backgroundColor: '#C9A961', color: '#0A2540' }}>Nuevo</span>
                </div>
                <p className="text-xs text-white/70 mt-0.5">Consulta tu portafolio y ejecuta ordenes hablando con IA. Primero en LATAM.</p>
              </div>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="flex-shrink-0"><path d="M9 18l6-6-6-6" stroke="#C9A961" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
          </button>

          {investorProfile && investorProfile.portfolio && PORTFOLIOS[investorProfile.portfolio] ? (() => {
            const p = PORTFOLIOS[investorProfile.portfolio];
            const legacy = p.composition || p.allocation || [];
            // Si el cliente ya tiene portafolio multimoneda, los ETFs salen de
            // AHI, no del legacy: en un Puente los ETFs pesan sobre el 60% en
            // dolares. Pintar el legacy (que suma 100) al lado de una barra que
            // dice "60% en dolares" es mostrarle dos verdades distintas.
            const colorDe = (sym) => legacy.find((x) => x.symbol === sym)?.color || p.color;
            const composition = portMulti?.posiciones
              ? portMulti.posiciones
                  .filter((x) => x.moneda === 'USD')
                  .map((x) => ({ symbol: x.ticker, pct: x.pct, color: colorDe(x.ticker) }))
              : legacy;
            return (
              <div>
                <div className="rounded-2xl p-5 mb-4" style={{ background: 'var(--fx-card)', border: '1px solid var(--fx-border)' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <PortfolioMark portfolio={investorProfile.portfolio} size={48} />
                    <div>
                      <h3 className="text-lg font-bold" style={{ color: 'var(--fx-text)' }}>
                        Portafolio {portMulti?.nombre || p.name}
                      </h3>
                      <p className="text-xs" style={{ color: 'var(--fx-muted)' }}>{p.desc}</p>
                    </div>
                  </div>
                  <div className="flex gap-4 mb-5">
                    <div className="flex-1 rounded-xl p-3 text-center" style={{ background: 'var(--fx-card-glass)', border: '1px solid var(--fx-border)' }}>
                      <div className="text-xs" style={{ color: 'var(--fx-muted)' }}>Riesgo</div>
                      <div className="text-sm font-bold" style={{ color: portfolioAccent(investorProfile.portfolio) }}>{p.risk}</div>
                    </div>
                    <div className="flex-1 rounded-xl p-3 text-center" style={{ background: 'var(--fx-card-glass)', border: '1px solid var(--fx-border)' }}>
                      <div className="text-xs" style={{ color: 'var(--fx-muted)' }}>Rendimiento</div>
                      <div className="text-sm font-bold" style={{ color: 'var(--fx-text)' }}>{p.expectedReturn}</div>
                    </div>
                  </div>
                  {/* LA MEZCLA DE MONEDAS. Sin esto la pantalla mostraba solo
                      los ETFs y parecia que existian "dos portafolios": el
                      Explorador de aqui y el Explorer Puente del perfilador.
                      Es el MISMO — este es el que le faltaba la capa de moneda. */}
                  {portMulti?.exposicion && (
                    <div className="mb-5 rounded-xl p-4" style={{ background: 'var(--fx-card-glass)', border: '1px solid var(--fx-border)' }}>
                      <div className="text-xs font-semibold mb-2" style={{ color: 'var(--fx-text)' }}>Cómo queda repartida tu plata</div>
                      <div className="flex h-2.5 rounded-full overflow-hidden mb-2">
                        <div style={{ width: `${portMulti.exposicion.cop}%`, background: '#F59E0B' }} />
                        <div style={{ width: `${portMulti.exposicion.usd}%`, background: '#2A59C6' }} />
                      </div>
                      <div className="flex justify-between text-xs mb-2">
                        <span className="text-[#B45309] font-semibold">{portMulti.exposicion.cop}% en pesos</span>
                        <span className="text-[#2A59C6] font-semibold">{portMulti.exposicion.usd}% en dólares</span>
                      </div>
                      <p className="text-[11px] leading-snug" style={{ color: 'var(--fx-muted)' }}>{portMulti.exposicion.porQue}</p>
                    </div>
                  )}

                  <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--fx-text)' }}>Distribución</h4>
                  {portMulti?.posiciones?.some((x) => x.moneda === 'COP') && (
                    <div className="mb-4">
                      <div className="text-xs font-semibold mb-2" style={{ color: 'var(--fx-muted)' }}>
                        En pesos · fondos de Progresión
                      </div>
                      <div className="space-y-2.5">
                        {portMulti.posiciones.filter((x) => x.moneda === 'COP').map((x) => {
                          const info = getPosicionInfo(x);
                          return (
                            <div key={x.fondo} className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex items-baseline gap-1.5 flex-wrap">
                                  <span className="text-sm font-bold text-[#B45309]">{x.fondo}</span>
                                  {info?.name && <span className="text-xs" style={{ color: 'var(--fx-muted)' }}>· {info.name}</span>}
                                </div>
                                {info?.what && (
                                  <div className="text-[11px] mt-0.5 leading-snug" style={{ color: 'var(--fx-muted)' }}>{info.what}</div>
                                )}
                              </div>
                              <span className="text-sm font-semibold shrink-0" style={{ color: 'var(--fx-muted)' }}>{x.pct}%</span>
                            </div>
                          );
                        })}
                      </div>
                      <p className="mt-2 text-[11px] text-[#B54708] bg-[#FFFAEB] border border-[#F4D68C] rounded-lg px-3 py-2">
                        Estamos terminando la conexión con Progresión: muy pronto los compras desde aquí.
                      </p>
                      <div className="text-xs font-semibold mt-4 mb-1" style={{ color: 'var(--fx-muted)' }}>
                        En dólares · ETFs y acciones
                      </div>
                    </div>
                  )}

                  <div className="space-y-2.5">
                    {composition.map((item) => {
                      // Enriquecer con info del ticker (nombre + qué hace).
                      // Si el ticker ya trae 'name' del PORTFOLIOS legacy, lo usa.
                      // Si no, busca en tickerInfo.js.
                      const info = getTickerInfo(item.symbol);
                      const displayName = item.name || info?.name || '';
                      const explanation = info?.what || '';
                      return (
                        <div key={item.symbol} className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                          <div className="flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-baseline gap-1.5 flex-wrap">
                                  <span
                                    className="text-sm font-bold text-[#2A59C6] cursor-pointer hover:underline"
                                    onClick={() => setTickerPopover(item.symbol)}
                                  >
                                    {item.symbol}
                                  </span>
                                  {displayName && (
                                    <span className="text-xs" style={{ color: 'var(--fx-muted)' }}>· {displayName}</span>
                                  )}
                                </div>
                                {explanation && (
                                  <div className="text-[11px] mt-0.5 leading-snug" style={{ color: 'var(--fx-muted)' }}>{explanation}</div>
                                )}
                              </div>
                              <span className="text-sm font-semibold flex-shrink-0" style={{ color: 'var(--fx-text)' }}>{item.pct}%</span>
                            </div>
                            <div className="mt-1.5 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--fx-border)' }}>
                              <div className="h-full rounded-full" style={{ width: `${item.pct}%`, backgroundColor: item.color }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Audio narrado del portafolio asignado al usuario.
                    DECISION ESTRATEGICA May 24 2026: el audio aqui SIEMPRE es PREMIUM
                    sin importar el tier del usuario.
                    Razon: el portafolio es el producto de adquisicion/onboarding, no de
                    retencion. Cliente nuevo necesita la explicacion completa (~5min con
                    bear case + pre-mortem + companero mental) para tomar la decision de
                    invertir su primer peso. Tier gating aqui mata conversion.
                    Stocks SIGUEN con tier gating — ahi si tiene sentido (research progresivo). */}
                {(() => {
                  // Con portafolio multimoneda se pide el audio DE ESE portafolio.
                  // Si todavia no existe, getPortfolioAudioUrl devuelve null y no
                  // se pinta nada — preferible a narrarle unos porcentajes que no
                  // son los que tiene en pantalla.
                  const audioUrl = getPortfolioAudioUrl(
                    investorProfile.portafolioId || investorProfile.portfolio, 'premium');
                  return audioUrl ? (
                    <HeroAudioPlayer
                      src={audioUrl}
                      tier="premium"
                      labelHint={`Portafolio ${portMulti?.nombre || p.name}`}
                      kind="portfolio"
                    />
                  ) : null;
                })()}

                {/* Estado en vivo del portafolio — health score, desviaciones, rebalanceo */}
                <PortfolioLiveWidget />

                {/* Análisis de El Oráculo para el portafolio */}
                <PortfolioAnalysis />
                <button
                  onClick={() => setShowRoboAdvisor(true)}
                  className="w-full py-3.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8] transition mb-3"
                >
                  Invertir en mi plan
                </button>

                {/* CTA Piloto Automatico */}
                <div
                  className="mb-3 p-4 rounded-xl border border-[#D1DFFF] bg-gradient-to-r from-[#F0F4FF] to-white cursor-pointer hover:shadow-sm transition"
                  onClick={() => setMainTab('piloto')}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-[#181D27]">Piloto Automatico</div>
                      <div className="text-xs text-[#6B7280] mt-0.5">
                        Invierte automaticamente cada semana sin pensar en el momento perfecto
                      </div>
                    </div>
                    <div className="text-xs text-[#2A59C6] font-semibold flex-shrink-0 ml-3">Activar ›</div>
                  </div>
                </div>

                <button
                  onClick={() => setShowOnboarding(true)}
                  className="w-full py-3 border border-[#E5E7EB] rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-[#F9FAFB] transition"
                >
                  Repetir cuestionario
                </button>
              </div>
            );
          })() : (
            <div className="max-w-lg">
              {/* Hero — descubrimiento de qué te espera al completar el cuestionario */}
              <div className="rounded-2xl p-6 mb-4 text-white" style={{ background: 'linear-gradient(135deg, #0a2540 0%, #1a3a6c 100%)' }}>
                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-2 font-semibold">
                  Tu portafolio personalizado te espera
                </div>
                <h3 className="text-xl font-bold mb-2 leading-snug">
                  En 7 preguntas te asignamos un plan de inversión
                </h3>
                <p className="text-sm opacity-90 leading-relaxed mb-4">
                  Tu plan viene con <strong>análisis institucional completo</strong> narrado por
                  el Oráculo — filosofía, apuestas centrales, comportamiento macro,
                  pre-mortem y bear case honesto. Audio de ~5 minutos para que
                  entiendas exactamente en qué estás invirtiendo antes de poner tu primer peso.
                </p>
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="px-6 py-3 bg-white text-[#0a2540] rounded-xl text-sm font-bold hover:bg-white/90 transition"
                >
                  Comenzar cuestionario →
                </button>
              </div>

              {/* Beneficios listados como confianza */}
              <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
                <div className="text-xs font-semibold text-[#181D27] mb-3">Lo que recibes al completarlo:</div>
                <ul className="space-y-2.5">
                  <li className="flex gap-2.5 text-xs text-[#4B5563]">
                    <span className="text-[#10B981] flex-shrink-0">✓</span>
                    <span>Composición sugerida con ETFs y acciones diversificadas</span>
                  </li>
                  <li className="flex gap-2.5 text-xs text-[#4B5563]">
                    <span className="text-[#10B981] flex-shrink-0">✓</span>
                    <span>Audio premium del Oráculo explicando tu portafolio</span>
                  </li>
                  <li className="flex gap-2.5 text-xs text-[#4B5563]">
                    <span className="text-[#10B981] flex-shrink-0">✓</span>
                    <span>Tesis personalizada según tu temperamento y plazo</span>
                  </li>
                  <li className="flex gap-2.5 text-xs text-[#4B5563]">
                    <span className="text-[#10B981] flex-shrink-0">✓</span>
                    <span>Acceso al glosario clickeable con definiciones simples</span>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {mainTab === 'portafolio' && (
        <Portfolio onSelectStock={(s) => { setSelected(s); setMainTab('mercado'); }} accountId={accountId} />
      )}

      {mainTab === 'mercado' && (
        <div className="mercado-ola">
          <div className="mercado-ola__head">
            <div>
              <h1>Acciones y ETFs</h1>
              <p>Lista simple. Precios en vivo cuando el mercado responde; compra y venta usan tus flujos reales.</p>
            </div>
            <div className="mercado-ola__links">
              <button type="button" className="mercado-ola__oraculo" onClick={() => setMainTab('oraculo')}>
                <img src={oraculoBrujula} alt="" />
                Pregúntale al Oráculo
              </button>
              <button type="button" className="mercado-ola__tchip" onClick={() => navigate('/dashboard/agentes')}>
                Mis Agentes
              </button>
            </div>
          </div>

          <div className="mercado-ola__search">
            <span className="mercado-ola__search-ico" aria-hidden>⌕</span>
            <input
              type="search"
              placeholder="Buscar acción o ETF"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={() => setTimeout(() => setSearchResults([]), 200)}
            />
            {searchResults.length > 0 && (
              <div className="mercado-ola__suggest">
                {searchResults.map((s) => (
                  <div
                    key={s.symbol}
                    onMouseDown={() => {
                      setFocusSymbol(s.symbol);
                      setCat(ETF_SYMBOLS.includes(s.symbol) ? 'etf' : 'trend');
                      setSearch('');
                      setSearchResults([]);
                    }}
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[rgba(61,139,253,0.08)]"
                  >
                    <span className="text-sm font-bold text-[var(--fx-accent)] w-16">{s.symbol}</span>
                    <span className="text-sm text-[var(--fx-muted)] truncate">{s.name || STOCK_NAMES[s.symbol] || ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mercado-ola__tabs" role="tablist" aria-label="Categorías">
            {cats.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`mercado-ola__tchip ${cat === c.id ? 'on' : ''}`}
                onClick={() => {
                  setCat(c.id);
                  setFocusSymbol(null);
                  if (c.id === 'col') setGeoFilter('colombia');
                  else setGeoFilter('internacional');
                }}
              >
                {c.label}
              </button>
            ))}
          </div>

          {cat === 'col' || geoFilter === 'colombia' ? (
            <ColombiaMarketPlaceholder />
          ) : cat === 'lists' ? (
            <div className="mercado-ola__panel">
              <h3 className="text-sm font-bold mb-2" style={{ color: 'var(--fx-text)' }}>Tus listas</h3>
              <p className="mercado-ola__hint" style={{ marginTop: 0 }}>
                Watchlists del usuario. Aún sin listas guardadas — crea «Dividendos» o «Tech» cuando el producto lo active. Sin tickers inventados.
              </p>
            </div>
          ) : loading ? (
            <div className="text-center py-10 text-[var(--fx-muted)]">Cargando datos de mercado...</div>
          ) : (
            <div className="mercado-ola__layout">
              <StockList
                symbols={listSymbols}
                snapshots={snapshots}
                activeSymbol={effectiveFocus}
                onSelect={(sym) => {
                  setFocusSymbol(sym);
                }}
              />
              <MarketDetailPanel
                symbol={effectiveFocus}
                snap={effectiveFocus ? snapshots[effectiveFocus] : null}
                bars={detailBars}
                period={detailPeriod}
                setPeriod={setDetailPeriod}
                onBuy={(sym) => setSelected(sym)}
                onSell={(sym) => setSelected(sym)}
              />
            </div>
          )}

          {cat !== 'col' && cat !== 'lists' && (
            <div className="mt-8">
              <NewsFeed onSymbolClick={(s) => { setFocusSymbol(s); setSelected(s); }} />
            </div>
          )}
        </div>
      )}

      {/* Ticker popover — se muestra cuando el usuario hace click en un ticker
          de la composicion del portafolio. Audio explicativo + boton para ir
          al analisis completo si esta en el catalogo del Cerebro 2.0. */}
      {tickerPopover && (
        <TickerPopover
          symbol={tickerPopover}
          onClose={() => setTickerPopover(null)}
          onSeeFullAnalysis={(sym) => {
            setSelected(sym);
            setMainTab('mercado');
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Mercado Colombia (BVC) — acciones y fondos colombianos. Datos demo hasta
// activar el acuerdo con Progresion SCB. Portado de v2 (dual-balance).
// ============================================================================
function getMarketStatus() {
  const now = new Date();
  const tzName = now.toLocaleString('en-US', { timeZone: 'America/New_York', timeZoneName: 'short' });
  const dst = tzName.includes('EDT');
  const bvcOpen = dst ? '8:30 AM COT' : '9:30 AM COT';
  const bvcClose = dst ? '3:00 PM COT' : '4:00 PM COT';
  const ny = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day = ny.getDay();
  const tm = ny.getHours() * 60 + ny.getMinutes();
  if (day === 0 || day === 6) return { open: false, bvc: `Cerrado, reabre lunes ${bvcOpen}`, nyse: 'Cerrado, reabre lunes 9:30 AM ET' };
  if (tm < 570) return { open: false, bvc: `Cerrado, abre hoy ${bvcOpen}`, nyse: 'Cerrado, abre hoy 9:30 AM ET' };
  if (tm >= 960) return { open: false, bvc: `Cerrado, reabre mañana ${bvcOpen}`, nyse: 'Cerrado, reabre mañana 9:30 AM ET' };
  return { open: true, bvc: `Abierto, cierra ${bvcClose}`, nyse: 'Abierto, cierra 4:00 PM ET' };
}

function ColombiaMarketPlaceholder() {
  const [colCat, setColCat] = useState('populares');
  const [modal, setModal] = useState(null);
  const populares = [
    { ticker: 'ECOPETROL', name: 'Ecopetrol S.A.', price: 2400, change: 0.52 },
    { ticker: 'BCOLOMBIA', name: 'Bancolombia', price: 35200, change: 1.18 },
    { ticker: 'ISA', name: 'Interconexión Eléctrica', price: 19850, change: -0.34 },
    { ticker: 'GRUPOSURA', name: 'Grupo SURA', price: 28100, change: 0.87 },
    { ticker: 'NUTRESA', name: 'Grupo Nutresa', price: 78900, change: 2.41 },
    { ticker: 'CEMARGOS', name: 'Cementos Argos', price: 4180, change: -1.05 },
  ];
  const masActivas = [
    { ticker: 'NUTRESA', name: 'Grupo Nutresa', price: 78900, change: 2.41 },
    { ticker: 'BCOLOMBIA', name: 'Bancolombia', price: 35200, change: 1.18 },
    { ticker: 'CEMARGOS', name: 'Cementos Argos', price: 4180, change: -1.05 },
    { ticker: 'GRUPOSURA', name: 'Grupo SURA', price: 28100, change: 0.87 },
    { ticker: 'ECOPETROL', name: 'Ecopetrol S.A.', price: 2400, change: 0.52 },
    { ticker: 'ISA', name: 'Interconexión Eléctrica', price: 19850, change: -0.34 },
  ];
  // Catálogo REAL de FICs de Progresión SCB (progresion.com.co, 16-jul-2026).
  // Rendimientos: demo hasta conectar NAV real; el Oráculo conoce estos mismos
  // 10 fondos (get_progresion_funds) y los explica en lenguaje sencillo.
  // Rentabilidades REALES: primero las VIVAS del backend (cron SFC diario ->
  // /api/market/fondos-progresion); este arreglo queda de respaldo (foto 14-jul).
  const [fondosVivos, setFondosVivos] = useState(null);
  useEffect(() => {
    let alive = true;
    getFondosProgresion().then((rows) => {
      if (alive && rows && rows.length >= 8) {
        setFondosVivos(rows.map((f) => ({
          ticker: (f.fondoId || f.nombre).toUpperCase().slice(0, 12),
          name: f.nombre,
          tipo: f.tipo,
          rendimiento: f.rentabilidadAnual != null ? `${f.rentabilidadAnual}% E.A.` : (f.rentabilidad_sfc ? f.rentabilidad_sfc.split(' (')[0] : 'ver ficha'),
          min: f.nombre === 'Rentar II' ? '$50.000 COP' : 'ficha técnica',
        })));
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const fondosRespaldo = [
    { ticker: 'RENTAR-II', name: 'Rentar II', tipo: 'Inmobiliario', rendimiento: '13.0% E.A.', min: '$50.000 COP' },
    { ticker: 'GS-ACCIONES', name: 'GS Acciones', tipo: 'Renta Variable Colombia', rendimiento: '36.9% E.A.', min: 'ficha técnica' },
    { ticker: 'GLOBAL-VISTA', name: 'Fondo Global Vista', tipo: 'Abierto (liquidez)', rendimiento: '7.7% E.A.', min: 'ficha técnica' },
    { ticker: 'RENTAMAS', name: 'Rentamás', tipo: 'Alternativo (perfil arriesgado)', rendimiento: '9.2% E.A.', min: 'ficha técnica' },
    { ticker: 'COF-TITULOS', name: 'COF Títulos Valores', tipo: 'Capital colectivo (deuda)', rendimiento: '8.7% E.A.', min: 'ficha técnica' },
    { ticker: 'RENTA-PLUS', name: 'Renta Plus', tipo: 'Mixto', rendimiento: '8.6% E.A.', min: 'ficha técnica' },
    { ticker: 'RENTA-CREDITO', name: 'Renta Crédito', tipo: 'Renta Fija (crédito)', rendimiento: '8.2% E.A.', min: 'ficha técnica' },
    { ticker: 'COF-FACTURAS', name: 'COF Facturas', tipo: 'Capital colectivo (facturas)', rendimiento: '8.1% E.A.', min: 'ficha técnica' },
    { ticker: 'RENTA-FIJA-MP', name: 'Renta Fija MP', tipo: 'Renta Fija mediano plazo', rendimiento: '6.8% E.A.', min: 'ficha técnica' },
    { ticker: 'RENTAR-2015', name: 'Rentar 2015', tipo: 'Inmobiliario (FCP)', rendimiento: '12.6% E.A.', min: 'ficha técnica' },
  ];
  const fondos = fondosVivos || fondosRespaldo;
  const fmtCop = (n) => `$${n.toLocaleString('es-CO')}`;
  function getFondosCutoff() {
    const colTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Bogota' }));
    const day = colTime.getDay();
    const tm = colTime.getHours() * 60 + colTime.getMinutes();
    if (day === 0 || day === 6) return 'Órdenes 24/7. Tu orden se ejecutará el lunes hábil con NAV del día.';
    if (tm < 720) return 'Órdenes 24/7. Antes de 12:00 PM COT se ejecutan hoy con NAV del día.';
    return 'Órdenes 24/7. Pasado el corte de 12:00 PM, tu orden se ejecutará mañana hábil.';
  }
  const mkt = getMarketStatus();
  const cutoffMsg = getFondosCutoff();
  const cats = [
    { id: 'populares', label: 'Populares' },
    { id: 'fondos', label: 'Fondos' },
    { id: 'movers', label: 'Más activas' },
  ];
  const onStock = (a) => {
    if (!mkt.open) {
      setModal({ title: 'Mercado cerrado', body: mkt.bvc + '. Podrás comprar acciones colombianas cuando el mercado reabra.' });
      return;
    }
    setModal({ title: `Comprar ${a.ticker}`, body: `La compra de acciones colombianas estará disponible cuando se active el acuerdo con Progresión SCB. Precio actual: ${fmtCop(a.price)}.` });
  };
  const onFondo = (f) => {
    setModal({ title: `Invertir en ${f.name}`, body: `La inversión en ${f.name} (${f.tipo}) estará disponible cuando se active el acuerdo con Progresión SCB. Rendimiento histórico: ${f.rendimiento}. Inversión mínima: ${f.min}. ${cutoffMsg}` });
  };
  const renderStocks = (list) => (
    <div className="space-y-2">
      {list.map((a) => (
        <div key={a.ticker} onClick={() => onStock(a)} className="bg-white border border-[#E5E7EB] rounded-xl p-3 flex items-center justify-between cursor-pointer hover:border-[#2A59C6] transition">
          <div>
            <div className="text-sm font-bold text-[#0A2540]">{a.ticker}</div>
            <div className="text-xs text-[#6B7280]">{a.name}</div>
          </div>
          <div className="text-right">
            <div className="text-sm font-bold">{fmtCop(a.price)}</div>
            <div className={`text-xs font-semibold ${a.change >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
              {a.change >= 0 ? '+' : ''}{a.change.toFixed(2)}%
            </div>
          </div>
        </div>
      ))}
    </div>
  );
  return (
    <div className="space-y-4">
      <div className={`rounded-xl p-3 flex items-center gap-2 text-xs font-semibold ${mkt.open ? 'bg-[#ECFDF5] border border-[#10B981] text-[#065F46]' : 'bg-[#FEF2F2] border border-[#FCA5A5] text-[#991B1B]'}`}>
        <span className={`w-2.5 h-2.5 rounded-full ${mkt.open ? 'bg-[#10B981]' : 'bg-[#DC2626]'}`}></span>
        BVC, {mkt.bvc}
      </div>
      <div className="bg-[#FFFBF0] border border-[#FCD34D] rounded-xl p-3 text-xs text-[#92400E]">
        Datos de demostración. La operación real se habilita con el acuerdo Progresión SCB.
      </div>
      <div className="flex gap-2 flex-wrap">
        {cats.map((c) => (
          <button key={c.id} onClick={() => setColCat(c.id)} className={`px-4.5 py-2 border rounded-full text-xs font-semibold ${colCat === c.id ? 'bg-[#2A59C6] text-white border-[#2A59C6]' : 'border-[#E5E7EB] text-[#6B7280] bg-white hover:border-[#2A59C6] hover:text-[#2A59C6]'} transition`}>
            {c.label}
          </button>
        ))}
      </div>
      {colCat === 'fondos' && (
        <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl p-3 text-xs text-[#1E40AF]">
          {cutoffMsg}
        </div>
      )}
      {colCat === 'populares' && renderStocks(populares)}
      {colCat === 'movers' && renderStocks(masActivas)}
      {colCat === 'fondos' && (
        <div className="space-y-2">
          {fondos.map((f) => (
            <div key={f.ticker} onClick={() => onFondo(f)} className="bg-white border border-[#E5E7EB] rounded-xl p-3 flex items-center justify-between cursor-pointer hover:border-[#2A59C6] transition">
              <div className="flex-1">
                <div className="text-sm font-bold text-[#0A2540]">{f.name}</div>
                <div className="text-xs text-[#6B7280]">{f.tipo} desde {f.min}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-[#059669]">{f.rendimiento}</div>
                <div className="text-xs text-[#9CA3AF]">rendimiento</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#0A2540] mb-2">{modal.title}</h3>
            <p className="text-sm text-[#6B7280] mb-4">{modal.body}</p>
            <button onClick={() => setModal(null)} className="w-full py-2.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8] transition">
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
